import React, { useState, useEffect, useCallback, useRef } from 'react';
import { collection, getDocs, doc, getDoc, setDoc, addDoc, updateDoc, deleteDoc, query, where } from 'firebase/firestore';
import { db, storage, functions } from '../firebase/firebaseConfig.js';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { getAuth, updatePassword, EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { FaUser, FaUsers, FaBuilding, FaCog, FaPlus, FaEdit, FaToggleOn, FaToggleOff, FaTimes, FaLock, FaUpload, FaDownload, FaSyncAlt, FaUserMd, FaTrash, FaUserPlus } from 'react-icons/fa';
import { Dialog, DialogBackdrop, DialogPanel } from '@headlessui/react';
import EmailRouting from './EmailRouting';
import { queueEmail } from '../utils/emailQueue';
// Security utilities
import { encryptCredentials, decryptCredentials } from '../utils/encryption';
import { logAdminAction, AUDIT_ACTIONS } from '../utils/auditLog';
import { toUserMessage } from '../utils/userError';
// Organization utilities for multi-tenancy filtering
import { matchesOrganization } from '../utils/organization';

const Settings = ({ setStep, userInfo, setUserInfo, activePractice = '', organization = 'development', onPracticeChange }) => {
  const [activeTab, setActiveTab] = useState('profile');
  const [selectedPractice, setSelectedPractice] = useState('');
  const [users, setUsers] = useState([]);
  const [practices, setPractices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({});
  const [editingUserId, setEditingUserId] = useState(null);
  const [editingPracticeId, setEditingPracticeId] = useState(null);
  const [isEditingUser, setIsEditingUser] = useState(false);
  const [isEditingPractice, setIsEditingPractice] = useState(false);
  const [isAddingPractice, setIsAddingPractice] = useState(false);
  const [isAddingUser, setIsAddingUser] = useState(false);
  const [profileData, setProfileData] = useState({});
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [isBulkUploading, setIsBulkUploading] = useState(false);
  const [bulkUploadProgress, setBulkUploadProgress] = useState({ success: 0, skipped: 0, errors: 0 });
  const [isBulkUploadingUsers, setIsBulkUploadingUsers] = useState(false);
  const [bulkUserUploadProgress, setBulkUserUploadProgress] = useState({ success: 0, skipped: 0, errors: 0 });
  const [isExportingUsers, setIsExportingUsers] = useState(false);
  const [isSendingResetLink, setIsSendingResetLink] = useState(false);
  const [expiringCredentials, setExpiringCredentials] = useState([]);
  const [isCheckingCredentials, setIsCheckingCredentials] = useState(false);
  const [originalCredentials, setOriginalCredentials] = useState(null); // Store original credentials when editing starts
  
  // Physician roster state (physicians without login accounts)
  const [physicians, setPhysicians] = useState([]);
  const [isAddingPhysician, setIsAddingPhysician] = useState(false);
  const [isEditingPhysician, setIsEditingPhysician] = useState(false);
  const [editingPhysicianId, setEditingPhysicianId] = useState(null);
  const [physicianFormData, setPhysicianFormData] = useState({});
  const [showPhysicianChoiceDialog, setShowPhysicianChoiceDialog] = useState(false);
  const [isCreatingPhysicianUser, setIsCreatingPhysicianUser] = useState(false); // When creating user from physician roster
  
  // Bulk selection state
  const [selectedPracticeIds, setSelectedPracticeIds] = useState(new Set());
  const [selectedUserIds, setSelectedUserIds] = useState(new Set());
  const [isBulkDeletingPractices, setIsBulkDeletingPractices] = useState(false);
  const [bulkDeletePracticeProgress, setBulkDeletePracticeProgress] = useState({ processed: 0, total: 0, deleted: 0, skipped: 0 });
  const [isBulkDeletingUsers, setIsBulkDeletingUsers] = useState(false);
  const [bulkDeleteUserProgress, setBulkDeleteUserProgress] = useState({ processed: 0, total: 0, deleted: 0 });
  const [confirmModal, setConfirmModal] = useState({ open: false, title: '', message: '', confirmLabel: 'Delete', onConfirm: null });

  // Ref to track if we've already fetched user info to prevent infinite loops
  const hasFetchedUserInfo = useRef(false);
  // Ref for synchronous guard - state updates are async, so isSendingResetLink alone
  // can't prevent double-invocation when something triggers the handler twice before re-render
  const isSendingResetLinkRef = useRef(false);
  const isSyncingPhysicianRosterRef = useRef(false);

  // Removed staticPractices - now using practices from database

  const roles = ["Standard", "Practice Administrator", "System Administrator"];
  const statuses = ["Pending", "Active", "Inactive"];

  // Determine available tabs based on user role
  const getAvailableTabs = () => {
    const tabs = [
      { id: 'profile', label: 'My Profile', icon: FaUser }
    ];

    if (userInfo.role === 'Practice Administrator') {
      tabs.push({ id: 'users', label: 'User Management', icon: FaUsers });
      tabs.push({ id: 'practice', label: 'Practice Settings', icon: FaBuilding });
    }

    if (userInfo.role === 'System Administrator') {
      tabs.push({ id: 'users', label: 'User Management', icon: FaUsers });
      tabs.push({ id: 'practices', label: 'Practice Management', icon: FaBuilding });
      tabs.push({ id: 'system', label: 'System Settings', icon: FaCog });
    }

    return tabs;
  };

  const availableTabs = getAvailableTabs();

  const formatPhoneNumber = (value) => {
    const phoneNumber = value.replace(/\D/g, '');
    if (phoneNumber.length <= 3) {
      return phoneNumber;
    } else if (phoneNumber.length <= 6) {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
    } else {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
    }
  };

  const normalizePracticeName = (value) => {
    if (!value) return '';
    return value.toString().trim().toLowerCase();
  };

  // Match user's practice (e.g. "Memorial Oral Surgery (Cypress)") to practice doc (practiceName only)
  const practiceMatchesTarget = (practice, targetPractice) => {
    if (!targetPractice) return false;
    const pName = (practice.practiceName || '').trim();
    const target = (targetPractice || '').trim();
    const n = normalizePracticeName(pName);
    const t = normalizePracticeName(target);
    return t === n || t.startsWith(n + ' (');
  };

  // Helper function to format date strings without timezone conversion issues
  // Date strings from Firestore are in YYYY-MM-DD format and should be treated as local dates
  const formatDateString = (dateString) => {
    if (!dateString) return '';
    // If it's already in YYYY-MM-DD format, parse it as local date
    if (typeof dateString === 'string' && dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const [year, month, day] = dateString.split('-').map(Number);
      const date = new Date(year, month - 1, day); // month is 0-indexed
      return date.toLocaleDateString();
    }
    // Otherwise, try to parse as Date and format
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString; // Return original if invalid
    return date.toLocaleDateString();
  };

  // Fetch latest userInfo from Firestore when component mounts (only once)
  // FIXED: Removed userInfo from dependencies to prevent infinite loop
  // The loop was: useEffect runs → setUserInfo() → userInfo changes → useEffect runs again
  useEffect(() => {
    // Don't reset profileData if user is currently editing their profile
    if (isEditingProfile) {
      return;
    }

    // Prevent fetching multiple times - only fetch once on mount
    if (hasFetchedUserInfo.current) {
      return;
    }

    const fetchLatestUserInfo = async () => {
      const auth = getAuth();
      const user = auth.currentUser;
      if (user) {
        try {
          const userDocRef = doc(db, 'users', user.uid);
          const userDocSnap = await getDoc(userDocRef);
          if (userDocSnap.exists()) {
            const userData = userDocSnap.data();
            // Get practices array, falling back to single practice for backward compatibility
            const userPractices = userData.practices || (userData.practice ? [userData.practice] : []);
            
            // Use the practice from Firestore data
            const currentPractice = userData.practice || '';
            
            const updatedUserInfo = {
              firstName: userData.firstName || '',
              lastName: userData.lastName || '',
              email: userData.email || '',
              mobile: userData.mobile || '',
              practice: currentPractice,
              practices: userPractices,
              role: userData.role || '',
              userType: userData.userType || 'Office Staff',
              credentials: userData.credentials || {},
              uid: user.uid
            };
            // Update parent component's userInfo if setUserInfo is available
            if (setUserInfo) {
              setUserInfo(updatedUserInfo);
            }
            // Also update local profileData
            let credentials = updatedUserInfo.credentials || {};
            if (updatedUserInfo.userType === 'Physician' && (!credentials || !credentials.medicalLicense)) {
              credentials = initializePhysicianCredentials();
            }
            setProfileData({
              firstName: updatedUserInfo.firstName,
              lastName: updatedUserInfo.lastName,
              email: updatedUserInfo.email,
              mobile: formatPhoneNumber(updatedUserInfo.mobile || ''),
              practice: updatedUserInfo.practice,
              practices: userPractices,
              role: updatedUserInfo.role,
              userType: updatedUserInfo.userType,
              credentials: credentials
            });
            // Mark as fetched to prevent re-running
            hasFetchedUserInfo.current = true;
            return;
          }
        } catch (error) {
          console.error('Error fetching latest user info:', error);
        }
      }
    };
    fetchLatestUserInfo();
  }, [isEditingProfile, setUserInfo]);

  // Separate effect to sync profileData from userInfo prop (for when parent updates it)
  // Only runs when userInfo changes AND we're not editing AND we've already done initial fetch
  useEffect(() => {
    if (isEditingProfile || !hasFetchedUserInfo.current) {
      return;
    }
    
    // Only sync profileData from prop, don't call setUserInfo to avoid loops
    let credentials = userInfo.credentials || {};
    if (userInfo.userType === 'Physician' && (!credentials || !credentials.medicalLicense)) {
      credentials = initializePhysicianCredentials();
    }
    const userPractices = userInfo.practices || (userInfo.practice ? [userInfo.practice] : []);
    setProfileData({
      firstName: userInfo.firstName || '',
      lastName: userInfo.lastName || '',
      email: userInfo.email || '',
      mobile: formatPhoneNumber(userInfo.mobile || ''),
      practice: userInfo.practice || '',
      practices: userPractices,
      role: userInfo.role || '',
      userType: userInfo.userType || 'Office Staff',
      credentials: credentials
    });
  }, [userInfo, isEditingProfile]);

  // Auto-check credentials when system settings tab is opened
  useEffect(() => {
    if (activeTab === 'system' && userInfo.role === 'System Administrator') {
      // Optionally auto-check on page load (commented out to avoid spam)
      // checkExpiringCredentials();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, userInfo.role]);

  const fetchUsers = useCallback(async () => {
    try {
      const usersCollection = collection(db, 'users');
      let userSnapshot;
      
      // We fetch all users and filter client-side to ensure the UI is consistent
      // The Firestore rules now allow Practice Admins to read users, but we still
      // filter the list to only show those relevant to the current practice.
      userSnapshot = await getDocs(usersCollection);
      
      const targetPractice = activePractice || userInfo.practice;
      const normalizedTargetPractice = normalizePracticeName(targetPractice);
      
      let userList = userSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Filter by organization first using smart inference for legacy documents
      userList = userList.filter(user => matchesOrganization(user, organization));

      // Filter users based on role and active practice
      if (userInfo.role === 'Practice Administrator') {
        // Practice Admins see users in the active practice AND themselves
        userList = userList.filter(user => {
          // Hide system admins from practice admins
          if (user.role === 'System Administrator') return false;
          
          // Always show yourself
          if ((user.email || '').toLowerCase() === (userInfo.email || '').toLowerCase()) return true;
          
          // Show users who belong to the target practice (primary or array)
          const normalizedUserPractice = normalizePracticeName(user.practice);
          const normalizedUserPractices = Array.isArray(user.practices)
            ? user.practices.map(normalizePracticeName)
            : [];
          const belongsToPractice = normalizedUserPractice === normalizedTargetPractice || 
                                   normalizedUserPractices.includes(normalizedTargetPractice) ||
                                   normalizedUserPractice.startsWith(normalizedTargetPractice + ' (') ||
                                   normalizedUserPractices.some(up => up.startsWith(normalizedTargetPractice + ' ('));
          
          return belongsToPractice;
        });
      } else if (userInfo.role === 'System Administrator') {
        // System Admins see all users, or filter by selected practice
        if (selectedPractice) {
          const normalizedSelectedPractice = normalizePracticeName(selectedPractice);
          userList = userList.filter(user => {
            const up = normalizePracticeName(user.practice);
            const ups = (user.practices || []).map(normalizePracticeName);
            const match = up === normalizedSelectedPractice || ups.includes(normalizedSelectedPractice) ||
              up.startsWith(normalizedSelectedPractice + ' (') || ups.some(u => u.startsWith(normalizedSelectedPractice + ' (')) ||
              normalizedSelectedPractice.startsWith(up + ' (');
            return match;
          });
        }
      } else {
        userList = [];
      }

      setUsers(userList);
    } catch (error) {
      console.error("Error fetching users: ", error);
      setUsers([]);
    }
  }, [selectedPractice, userInfo, activePractice, organization]);

  const fetchPractices = useCallback(async () => {
    try {
      const practicesCollection = collection(db, 'practices');
      const practiceSnapshot = await getDocs(practicesCollection);
      const practiceList = practiceSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      const filteredPractices = practiceList.filter(practice => {
        // Filter by organization first using smart inference for legacy documents
        if (!matchesOrganization(practice, organization)) {
          return false;
        }

        if (userInfo.role === 'System Administrator') {
          return true;
        } else if (userInfo.role === 'Practice Administrator') {
          const targetPractice = activePractice || userInfo.practice;
          return practiceMatchesTarget(practice, targetPractice);
        } else {
          return false;
        }
      });

      setPractices(filteredPractices);
    } catch (error) {
      console.error("Error fetching practices: ", error);
    }
  }, [userInfo, activePractice, organization]);

  // Fetch physicians for the practice roster (physicians without login accounts)
  // Supports multi-practice physicians via 'practices' array
  const fetchPhysicians = useCallback(async () => {
    try {
      const physiciansCollection = collection(db, 'physicians');
      const physicianSnapshot = await getDocs(physiciansCollection);
      
      const allPhysicians = physicianSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // First filter by organization using smart inference for legacy documents
      const organizationPhysicians = allPhysicians.filter(physician => matchesOrganization(physician, organization));
      
      // Filter physicians based on user role and active practice
      // For Practice Admins, use the activePractice from the toggle (not their primary practice)
      let filteredPhysicians = [];
      const targetPractice = userInfo.role === 'System Administrator' 
        ? selectedPractice 
        : (activePractice || userInfo.practice);
      
      if (userInfo.role === 'System Administrator' && !selectedPractice) {
        // System admins with no filter see all physicians in their organization
        filteredPhysicians = organizationPhysicians;
      } else if (targetPractice) {
        // Filter physicians that belong to the target practice
        filteredPhysicians = organizationPhysicians.filter(physician => {
          // Support both old format (practice string) and new format (practices array)
          if (physician.practices && Array.isArray(physician.practices)) {
            return physician.practices.some(p => p.name === targetPractice);
          }
          // Fallback for old format
          return physician.practice === targetPractice;
        });
      }
      
      // Add computed fields for display
      const physiciansWithStatus = filteredPhysicians.map(physician => {
        let statusForPractice = 'Active';
        let allPractices = [];
        
        if (physician.practices && Array.isArray(physician.practices)) {
          allPractices = physician.practices;
          const practiceEntry = physician.practices.find(p => p.name === targetPractice);
          if (practiceEntry) {
            statusForPractice = practiceEntry.status || 'Active';
          }
        } else {
          // Fallback for old format
          statusForPractice = physician.status || 'Active';
          allPractices = [{ name: physician.practice, status: statusForPractice }];
        }
        
        return {
          ...physician,
          statusForPractice,
          allPractices,
          practiceCount: allPractices.length
        };
      });
      
      // Sort by last name, then first name
      physiciansWithStatus.sort((a, b) => {
        const lastNameCompare = (a.lastName || '').localeCompare(b.lastName || '');
        if (lastNameCompare !== 0) return lastNameCompare;
        return (a.firstName || '').localeCompare(b.firstName || '');
      });
      
      setPhysicians(physiciansWithStatus);
    } catch (error) {
      console.error("Error fetching physicians: ", error);
    }
  }, [userInfo, selectedPractice, activePractice, organization]);

  // Ensure physician-type users always appear in physician roster for the current scope.
  const syncPhysicianUsersToRoster = useCallback(async () => {
    if (isSyncingPhysicianRosterRef.current || users.length === 0) return;

    const normalizeEmail = (value) => (value || '').toString().trim().toLowerCase();
    const getUserPractices = (user) => {
      const list = [];
      if (user.practice) list.push(user.practice);
      if (Array.isArray(user.practices)) list.push(...user.practices);
      return Array.from(
        new Set(
          list
            .map(p => (p || '').toString().trim())
            .filter(Boolean)
        )
      );
    };

    const targetPractice = userInfo.role === 'System Administrator'
      ? selectedPractice
      : (activePractice || userInfo.practice);
    const normalizedTargetPractice = normalizePracticeName(targetPractice);

    const physicianUsers = users.filter(user => user.userType === 'Physician' && normalizeEmail(user.email));
    if (physicianUsers.length === 0) return;

    try {
      isSyncingPhysicianRosterRef.current = true;

      const physiciansCollection = collection(db, 'physicians');
      const physicianSnapshot = await getDocs(physiciansCollection);
      const existingByEmail = new Map();

      physicianSnapshot.docs.forEach(snapshotDoc => {
        const physicianData = snapshotDoc.data();
        // Filter by organization using smart inference for legacy documents
        if (!matchesOrganization(physicianData, organization)) return;

        const physicianEmail = normalizeEmail(physicianData.email);
        if (!physicianEmail) return;
        existingByEmail.set(physicianEmail, { id: snapshotDoc.id, ...physicianData });
      });

      let didWrite = false;
      const nowIso = new Date().toISOString();
      const updatedBy = userInfo.email || 'system';

      for (const physicianUser of physicianUsers) {
        const email = normalizeEmail(physicianUser.email);
        const userPractices = getUserPractices(physicianUser);
        const practicesForScope = targetPractice
          ? userPractices.filter(practiceName => {
            const normalizedPractice = normalizePracticeName(practiceName);
            return normalizedPractice === normalizedTargetPractice ||
              normalizedPractice.startsWith(normalizedTargetPractice + ' (');
          })
          : userPractices;

        // If current scope is a specific practice, ensure this exact practice is represented on roster.
        if (targetPractice && practicesForScope.length > 0 && !practicesForScope.includes(targetPractice)) {
          practicesForScope.push(targetPractice);
        }

        if (practicesForScope.length === 0) continue;

        const existingPhysician = existingByEmail.get(email);
        if (!existingPhysician) {
          await addDoc(physiciansCollection, {
            firstName: physicianUser.firstName || '',
            lastName: physicianUser.lastName || '',
            email,
            specialty: physicianUser.credentials?.specialty || '',
            npi: physicianUser.credentials?.npiNumber || '',
            practices: practicesForScope.map(name => ({ name, status: physicianUser.status || 'Active' })),
            organization,
            createdAt: nowIso,
            createdBy: updatedBy,
            linkedUserId: physicianUser.id || null
          });
          didWrite = true;
          continue;
        }

        let currentPractices = Array.isArray(existingPhysician.practices) ? [...existingPhysician.practices] : [];
        if (currentPractices.length === 0 && existingPhysician.practice) {
          currentPractices = [{ name: existingPhysician.practice, status: existingPhysician.status || 'Active' }];
        }
        const existingPracticeNames = new Set(currentPractices.map(p => p?.name).filter(Boolean));
        const missingPractices = practicesForScope.filter(name => !existingPracticeNames.has(name));

        const profileChanged =
          (physicianUser.firstName || '') !== (existingPhysician.firstName || '') ||
          (physicianUser.lastName || '') !== (existingPhysician.lastName || '') ||
          (physicianUser.id || null) !== (existingPhysician.linkedUserId || null);

        if (missingPractices.length > 0 || profileChanged) {
          if (missingPractices.length > 0) {
            currentPractices.push(...missingPractices.map(name => ({ name, status: physicianUser.status || 'Active' })));
          }
          await updateDoc(doc(db, 'physicians', existingPhysician.id), {
            firstName: physicianUser.firstName || existingPhysician.firstName || '',
            lastName: physicianUser.lastName || existingPhysician.lastName || '',
            practices: currentPractices,
            linkedUserId: physicianUser.id || existingPhysician.linkedUserId || null,
            organization,
            updatedAt: nowIso,
            updatedBy
          });
          didWrite = true;
        }
      }

      if (didWrite) {
        await fetchPhysicians();
      }
    } catch (error) {
      console.error('Error syncing physician users to roster:', error);
    } finally {
      isSyncingPhysicianRosterRef.current = false;
    }
  }, [users, userInfo, selectedPractice, activePractice, organization, fetchPhysicians]);

  useEffect(() => {
    fetchUsers();
    fetchPractices();
    fetchPhysicians();
    setLoading(false);
  }, [fetchUsers, fetchPractices, fetchPhysicians]);

  useEffect(() => {
    syncPhysicianUsersToRoster();
  }, [syncPhysicianUsersToRoster]);

  // Helper function to get list of credentials that changed and are within 3 months
  // Returns an array of credential names that should trigger notifications
  const getChangedExpiringCredentials = (original, updated) => {
    if (!updated) return [];
    
    const threeMonthsFromNow = new Date();
    threeMonthsFromNow.setMonth(threeMonthsFromNow.getMonth() + 3);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const changedCredentials = [];
    
    // Helper to check if a date is within 3 months
    const isWithinThreeMonths = (expirationDate) => {
      if (!expirationDate) return false;
      const expDate = new Date(expirationDate);
      expDate.setHours(0, 0, 0, 0);
      return expDate <= threeMonthsFromNow && expDate >= today;
    };
    
    // Helper to check if expiration date changed
    const expirationDateChanged = (origDate, updDate) => {
      // Normalize dates for comparison (handle both string and Date objects)
      const normalizeDate = (date) => {
        if (!date) return '';
        if (typeof date === 'string') return date.trim();
        return date;
      };
      
      const orig = normalizeDate(origDate);
      const upd = normalizeDate(updDate);
      
      if (!orig && !upd) return false; // Both empty, no change
      if (!orig && upd) return true; // Added new expiration date
      if (orig && !upd) return false; // Removed expiration date (not a concern for notifications)
      return orig !== upd; // Changed expiration date
    };
    
    // Check Medical License - only if expiration date changed/added AND is within 3 months
    if (expirationDateChanged(original?.medicalLicense?.expirationDate, updated.medicalLicense?.expirationDate)) {
      if (isWithinThreeMonths(updated.medicalLicense?.expirationDate)) {
        changedCredentials.push('medicalLicense');
      }
    }
    
    // Check Dental License - only if expiration date changed/added AND is within 3 months
    if (expirationDateChanged(original?.dentalLicense?.expirationDate, updated.dentalLicense?.expirationDate)) {
      if (isWithinThreeMonths(updated.dentalLicense?.expirationDate)) {
        changedCredentials.push('dentalLicense');
      }
    }
    
    // Check DEA Certification - only if expiration date changed/added AND is within 3 months
    if (expirationDateChanged(original?.deaCertification?.expirationDate, updated.deaCertification?.expirationDate)) {
      if (isWithinThreeMonths(updated.deaCertification?.expirationDate)) {
        changedCredentials.push('deaCertification');
      }
    }
    
    // Check Malpractice Insurance - only if expiration date changed/added AND is within 3 months
    if (expirationDateChanged(original?.malpracticeInsurance?.expirationDate, updated.malpracticeInsurance?.expirationDate)) {
      if (isWithinThreeMonths(updated.malpracticeInsurance?.expirationDate)) {
        changedCredentials.push('malpracticeInsurance');
      }
    }
    
    return changedCredentials; // Return list of changed credentials
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      
      if (user) {
        try {
          const userDocRef = doc(db, 'users', user.uid);
          const userDocSnap = await getDoc(userDocRef);
          
          if (userDocSnap.exists()) {
            // Check if credentials need to be initialized for physicians
            const updateData = { ...profileData };
            if (updateData.userType === 'Physician' && (!updateData.credentials || !updateData.credentials.medicalLicense)) {
              updateData.credentials = updateData.credentials || initializePhysicianCredentials();
            }
            
            // Encrypt credentials before storing (license numbers, DEA, etc.)
            if (updateData.credentials) {
              updateData.credentials = await encryptCredentials(updateData.credentials);
            }
            
            await updateDoc(userDocRef, updateData);
            
            // Log admin action for audit trail
            await logAdminAction(AUDIT_ACTIONS.USER_UPDATED, user.uid, {
              action: 'profile_update',
              fields: Object.keys(updateData)
            });
            
            toast.success('Profile updated successfully!');
            
            // Only check for expiring credentials if expiration dates within 3 months changed or were added
            if (updateData.userType === 'Physician' && updateData.credentials) {
              const changedCredentials = originalCredentials 
                ? getChangedExpiringCredentials(originalCredentials, updateData.credentials)
                : null; // If no original credentials stored, check all (first time setup)
              
              // Only send notification if there are changed credentials within 3 months, or if it's first time setup
              // If changedCredentials is an empty array, it means credentials changed but none are within 3 months - don't send email
              if (changedCredentials === null) {
                // First time setup - check all credentials
                await checkAndNotifyExpiringCredentials(
                  updateData, 
                  `${updateData.firstName || ''} ${updateData.lastName || ''}`.trim() || updateData.email, 
                  true,
                  null // Check all credentials
                );
              } else if (changedCredentials.length > 0) {
                // Only check the specific credentials that changed and are within 3 months
                await checkAndNotifyExpiringCredentials(
                  updateData, 
                  `${updateData.firstName || ''} ${updateData.lastName || ''}`.trim() || updateData.email, 
                  true,
                  changedCredentials // Pass the list of changed credentials to only check those
                );
              }
              // If changedCredentials.length === 0, do nothing (credentials changed but none are within 3 months)
            }
            
            // Clear original credentials after successful update
            setOriginalCredentials(null);
            
            // Update userInfo if needed
            if (setUserInfo) {
              setUserInfo(prev => ({
                ...prev,
                ...updateData
              }));
            } else if (userInfo) {
              Object.assign(userInfo, updateData);
            }
            
            // If practices were updated and there's no active practice, set one
            if (updateData.practices && updateData.practices.length > 0 && onPracticeChange) {
              // If current activePractice is not in the new practices list, or there's no active practice
              if (!activePractice || !updateData.practices.includes(activePractice)) {
                // Set the first practice as active
                onPracticeChange(updateData.practices[0]);
              }
            }
            
            // Refresh profileData from Firestore after successful update
            const updatedDocSnap = await getDoc(userDocRef);
            if (updatedDocSnap.exists()) {
              const updatedUserData = updatedDocSnap.data();
              let credentials = updatedUserData.credentials || {};
              if (updatedUserData.userType === 'Physician' && (!credentials || !credentials.medicalLicense)) {
                credentials = initializePhysicianCredentials();
              }
              setProfileData({
                firstName: updatedUserData.firstName || '',
                lastName: updatedUserData.lastName || '',
                email: updatedUserData.email || '',
                mobile: formatPhoneNumber(updatedUserData.mobile || ''),
                practice: updatedUserData.practice || '',
                role: updatedUserData.role || '',
                userType: updatedUserData.userType || 'Office Staff',
                credentials: credentials
              });
            }
            
            setIsEditingProfile(false);
          } else {
            toast.error('User document not found');
          }
        } catch (error) {
          toast.error(toUserMessage(error, 'Unable to update your profile. Please try again.'));
        }
      }
    } catch (error) {
      toast.error(toUserMessage(error, 'Unable to update your profile. Please try again.'));
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('New passwords do not match!');
      return;
    }

    try {
      const auth = getAuth();
      const user = auth.currentUser;
      
      if (user) {
        const credential = EmailAuthProvider.credential(
          user.email,
          passwordData.currentPassword
        );
        
        await reauthenticateWithCredential(user, credential);
        await updatePassword(user, passwordData.newPassword);
        toast.success('Password changed successfully!');
        setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
        setIsChangingPassword(false);
      }
    } catch (error) {
      toast.error(toUserMessage(error, 'Unable to change your password. Please try again.'));
    }
  };
  
  const handleDisableUser = async (userId) => {
    try {
      const userRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userRef);
      const userData = userDoc.data();
      const currentStatus = userData.status;
      const newStatus = currentStatus === 'Active' ? 'Inactive' : 'Active';

      await updateDoc(userRef, { status: newStatus });

      // Sync status to physician roster when user has a linked physician entry (matched by email)
      if (userData.email) {
        await syncUserStatusToPhysicianRoster(userData.email, newStatus);
      }

      await fetchUsers();
      fetchPhysicians(); // Refresh physician roster so status change is visible
      toast.success(`User ${currentStatus === 'Active' ? 'deactivated' : 'activated'} successfully!`);
    } catch (error) {
      toast.error(toUserMessage(error, 'Unable to update that user. Please try again.'));
    }
  };

  // Check if current user can delete a specific user
  const canDeleteUser = (targetUser) => {
    // System Admins can delete any user except other System Admins
    if (userInfo.role === 'System Administrator') {
      return targetUser.role !== 'System Administrator';
    }
    // Practice Admins can delete users in their practice except Practice Admins
    if (userInfo.role === 'Practice Administrator') {
      return targetUser.practice === userInfo.practice && 
             targetUser.role !== 'Practice Administrator' &&
             targetUser.role !== 'System Administrator';
    }
    return false;
  };

  // Delete user handler - removes from both Firebase Auth, Firestore, and physician roster
  const handleDeleteUser = async (userId, userName) => {
    const confirmed = window.confirm(
      `This action will permanently delete the user "${userName}". Are you sure you want to continue?`
    );

    if (!confirmed) return;

    try {
      const targetUser = users.find(u => u.id === userId);
      const deleteUserFn = httpsCallable(functions, 'deleteUser');
      await deleteUserFn({ userId });

      if (targetUser?.email) {
        await removeUserFromPhysicianRoster(targetUser.email);
      }

      await logAdminAction(AUDIT_ACTIONS.USER_DELETED || 'USER_DELETED', userId, {
        deletedBy: userInfo.email,
        deletedUserName: userName
      });

      await fetchUsers();
      await fetchPhysicians();
      toast.success(`User "${userName}" has been permanently deleted.`);
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error(toUserMessage(error, 'Unable to delete that user. Please try again.'));
    }
  };

  // Delete practice handler (System Admin only)
  const handleDeletePractice = async (practiceId, practiceName) => {
    const confirmed = window.confirm(
      `This action will permanently delete the practice "${practiceName}". Are you sure you want to continue?`
    );
    
    if (!confirmed) return;
    
    try {
      // Check if there are users still assigned to this practice (including legacy "Name (Location)" format)
      const usersInPractice = users.filter(u => {
        const uPractice = (u.practice || '').trim();
        const uPractices = (u.practices || []).map(p => (p || '').trim());
        const n = normalizePracticeName(practiceName);
        return uPractice === practiceName || uPractices.includes(practiceName) ||
          normalizePracticeName(uPractice) === n || uPractices.some(p => normalizePracticeName(p) === n) ||
          normalizePracticeName(uPractice).startsWith(n + ' (') || uPractices.some(p => normalizePracticeName(p).startsWith(n + ' ('));
      });
      if (usersInPractice.length > 0) {
        toast.error(`Cannot delete practice "${practiceName}". There are ${usersInPractice.length} user(s) still assigned to it. Please reassign or delete those users first.`);
        return;
      }
      
      // Delete from Firestore
      const practiceRef = doc(db, 'practices', practiceId);
      await deleteDoc(practiceRef);
      
      // Log the action
      await logAdminAction(AUDIT_ACTIONS.PRACTICE_DELETED || 'PRACTICE_DELETED', practiceId, {
        deletedBy: userInfo.email,
        deletedPracticeName: practiceName
      });
      
      await fetchPractices();
      toast.success(`Practice "${practiceName}" has been permanently deleted.`);
    } catch (error) {
      console.error('Error deleting practice:', error);
      toast.error(toUserMessage(error, 'Unable to delete that practice. Please try again.'));
    }
  };
  
  // Bulk delete practices (System Admin only)
  const handleBulkDeletePractices = async () => {
    if (selectedPracticeIds.size === 0) return;
    const count = selectedPracticeIds.size;
    setConfirmModal({
      open: true,
      title: 'Delete Practices',
      message: `This will permanently delete ${count} practice${count !== 1 ? 's' : ''}. Practices with assigned users will be skipped. This action cannot be undone.`,
      confirmLabel: 'Delete All',
      onConfirm: async () => {
        setConfirmModal(prev => ({ ...prev, open: false }));

        setIsBulkDeletingPractices(true);
        setBulkDeletePracticeProgress({ processed: 0, total: count, deleted: 0, skipped: 0 });

        let deleted = 0;
        let skipped = 0;
        let processed = 0;
        const ids = [...selectedPracticeIds];
        for (const practiceId of ids) {
          try {
            const p = practices.find(pr => pr.id === practiceId);
            const pName = p?.practiceName || practiceId;
            const n = normalizePracticeName(pName);
            const usersInPractice = users.filter(u => {
              const uPractice = (u.practice || '').trim();
              const uPractices = (u.practices || []).map(x => (x || '').trim());
              return uPractice === pName || uPractices.includes(pName) ||
                normalizePracticeName(uPractice) === n || uPractices.some(x => normalizePracticeName(x) === n) ||
                normalizePracticeName(uPractice).startsWith(n + ' (') || uPractices.some(x => normalizePracticeName(x).startsWith(n + ' ('));
            });
            if (usersInPractice.length > 0) {
              skipped++;
            } else {
              await deleteDoc(doc(db, 'practices', practiceId));
              await logAdminAction(AUDIT_ACTIONS.PRACTICE_DELETED || 'PRACTICE_DELETED', practiceId, {
                deletedBy: userInfo.email, deletedPracticeName: pName
              });
              deleted++;
            }
          } catch (error) {
            console.error(`Error deleting practice ${practiceId}:`, error);
          }
          processed++;
          setBulkDeletePracticeProgress({ processed, total: count, deleted, skipped });
        }
        setSelectedPracticeIds(new Set());
        await fetchPractices();
        setIsBulkDeletingPractices(false);
        setBulkDeletePracticeProgress({ processed: 0, total: 0, deleted: 0, skipped: 0 });
        let msg = `Deleted ${deleted} practice${deleted !== 1 ? 's' : ''}.`;
        if (skipped > 0) msg += ` ${skipped} skipped (have assigned users).`;
        toast.success(msg);
      }
    });
  };

  // Bulk delete users
  const handleBulkDeleteUsers = async () => {
    if (selectedUserIds.size === 0) return;
    const count = selectedUserIds.size;
    setConfirmModal({
      open: true,
      title: 'Delete Users',
      message: `This will permanently delete ${count} user${count !== 1 ? 's' : ''} and remove them from the physician roster. This action cannot be undone.`,
      confirmLabel: 'Delete All',
      onConfirm: async () => {
        setConfirmModal(prev => ({ ...prev, open: false }));

        setIsBulkDeletingUsers(true);
        setBulkDeleteUserProgress({ processed: 0, total: count, deleted: 0 });

        let deleted = 0;
        let processed = 0;
        const deleteUserFn = httpsCallable(functions, 'deleteUser');
        const ids = [...selectedUserIds];
        for (const userId of ids) {
          try {
            const u = users.find(usr => usr.id === userId);
            await deleteUserFn({ userId });
            if (u?.email) {
              await removeUserFromPhysicianRoster(u.email);
            }
            await logAdminAction(AUDIT_ACTIONS.USER_DELETED || 'USER_DELETED', userId, {
              deletedBy: userInfo.email,
              deletedUserName: u ? `${u.firstName} ${u.lastName}` : userId
            });
            deleted++;
          } catch (error) {
            console.error(`Error deleting user ${userId}:`, error);
          }
          processed++;
          setBulkDeleteUserProgress({ processed, total: count, deleted });
        }
        setSelectedUserIds(new Set());
        await fetchUsers();
        await fetchPhysicians();
        setIsBulkDeletingUsers(false);
        setBulkDeleteUserProgress({ processed: 0, total: 0, deleted: 0 });
        toast.success(`Deleted ${deleted} user${deleted !== 1 ? 's' : ''}.`);
      }
    });
  };

  const handleEditUser = async (userId) => {
    try {
      const userRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userRef);

      if (userDoc.exists()) {
        const userData = userDoc.data();
        // Store original credentials for comparison when editing existing user
        if (userData.userType === 'Physician' && userData.credentials) {
          setOriginalCredentials(JSON.parse(JSON.stringify(userData.credentials)));
        } else {
          setOriginalCredentials(null);
        }
        // Initialize credentials structure if user is physician but credentials don't exist
        let credentials = userData.credentials || {};
        if (userData.userType === 'Physician' && !credentials.medicalLicense) {
          credentials = initializePhysicianCredentials();
        }
        // Ensure practices array is initialized for backward compatibility
        const userPractices = userData.practices || (userData.practice ? [userData.practice] : []);
        setFormData({
          ...userData,
          practices: userPractices,
          credentials: credentials,
          userType: userData.userType || 'Office Staff'
        });
        setEditingUserId(userId);
        setIsCreatingPhysicianUser(false); // Reset physician-only mode when editing
        setIsEditingUser(true);
      }
    } catch (error) {
      console.error("Error fetching user data: ", error);
    }
  };

  const handleAddNewUser = () => {
    const defaultPractice = userInfo.role === 'Practice Administrator' ? (activePractice || userInfo.practice) : '';
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      mobile: '',
      practice: defaultPractice, // Keep for backward compatibility
      practices: defaultPractice ? [defaultPractice] : [], // New multi-practice support
      role: 'Standard',
      status: 'Pending',
      userType: 'Office Staff',
      credentials: initializePhysicianCredentials()
    });
    setIsCreatingPhysicianUser(false); // Reset physician-only mode
    setIsAddingUser(true);
  };

  const handleSubmitUser = async (e) => {
    e.preventDefault();

    try {
      if (isAddingUser) {
        // Validate temp password for new users
        if (!formData.tempPassword || !formData.confirmTempPassword) {
          toast.error('Temporary password is required for new users.');
          return;
        }
        if (formData.tempPassword !== formData.confirmTempPassword) {
          toast.error('Temporary passwords do not match.');
          return;
        }

        // Create user via Cloud Function (secure - uses Admin SDK server-side)
        // This prevents unauthorized user creation via tools like firepwn
        let authUserUid;
        try {
          const createUserFn = httpsCallable(functions, 'createUser');
          const result = await createUserFn({
            email: formData.email.toLowerCase().trim(),
            password: formData.tempPassword,
            userData: {
              practice: formData.practice,
              practices: formData.practices,
              organization: organization
            }
          });
          authUserUid = result.data.uid;
        } catch (fnError) {
          console.error('Error creating user:', fnError);
          // Handle specific error codes from our Cloud Function
          if (fnError.code === 'functions/already-exists') {
            toast.error('This email is already registered.');
            return;
          }
          if (fnError.code === 'functions/permission-denied') {
            toast.error(toUserMessage(fnError, 'You do not have permission to create users.'));
            return;
          }
          if (fnError.code === 'functions/invalid-argument') {
            toast.error(toUserMessage(fnError, 'Some of the user information is not valid.'));
            return;
          }
          toast.error('Failed to create user. Please try again.');
          return;
        }

        // Save user info to Firestore (excluding password fields)
        const { tempPassword, confirmTempPassword, ...userDataWithoutPassword } = formData;
        
        // Encrypt credentials before storing (license numbers, DEA, etc.)
        let encryptedCredentials = {};
        if (formData.userType === 'Physician') {
          const rawCredentials = formData.credentials || initializePhysicianCredentials();
          encryptedCredentials = await encryptCredentials(rawCredentials);
        }
        
        // Use first practice as primary for backward compatibility
        const primaryPractice = formData.practices && formData.practices.length > 0 
          ? formData.practices[0] 
          : formData.practice || '';
        
        await setDoc(doc(db, 'users', authUserUid), {
          ...userDataWithoutPassword,
          email: formData.email.toLowerCase().trim(),
          organization: organization, // Organization/tenant identifier
          practice: primaryPractice, // Primary practice for backward compatibility
          practices: formData.practices || (formData.practice ? [formData.practice] : []), // All assigned practices
          userType: formData.userType || 'Office Staff',
          credentials: encryptedCredentials,
          createdAt: new Date(),
          createdBy: userInfo.email,
          status: formData.status || 'Active',
          requiresPasswordChange: true // First login must change temp password
        });
        
        // Log admin action for audit trail
        await logAdminAction(AUDIT_ACTIONS.USER_CREATED, authUserUid, {
          createdBy: userInfo.email,
          userType: formData.userType
        });

        // Send new-user set-password email through Firestore mail pipeline.
        try {
          const sendPasswordResetFn = httpsCallable(functions, 'sendPasswordResetEmailForUser');
          await sendPasswordResetFn({
            userId: authUserUid,
            email: formData.email.toLowerCase().trim(),
            originUrl: window.location.origin,
            purpose: 'welcome'
          });
        } catch (emailError) {
          console.error('Error sending password reset email:', emailError);
        }

        // For new users, always check credentials (first time setup)
        // Check if any credentials are expiring within 3 months and send email (immediate check)
        if (formData.userType === 'Physician' && formData.credentials) {
          await checkAndNotifyExpiringCredentials(formData, `${formData.firstName || ''} ${formData.lastName || ''}`.trim() || formData.email, true);
        }
        
        // If user type is Physician, automatically add them to the physicians roster for all assigned practices
        if (formData.userType === 'Physician') {
          const hasAnyPractice = (formData.practices && formData.practices.length > 0) || formData.practice;
          if (hasAnyPractice) {
            await addPhysicianToRoster({ ...formData, uid: authUserUid });
          }
        }

        setIsEditingUser(false);
        setIsAddingUser(false);
        setIsCreatingPhysicianUser(false); // Reset physician-only mode
        setOriginalCredentials(null);
        setTimeout(() => {
          toast.success('User added successfully!');
          fetchUsers();
          fetchPhysicians(); // Refresh physicians list
        }, 200);
      } else {
        // Editing existing user - no password fields, just profile data
        const { tempPassword, confirmTempPassword, ...userDataWithoutPassword } = formData;
        const userRef = doc(db, 'users', editingUserId);
        
        // Encrypt credentials before storing (license numbers, DEA, etc.)
        let encryptedCredentials = {};
        if (formData.userType === 'Physician') {
          const rawCredentials = formData.credentials || initializePhysicianCredentials();
          encryptedCredentials = await encryptCredentials(rawCredentials);
        }
        
        // Use first practice as primary for backward compatibility
        const primaryPractice = formData.practices && formData.practices.length > 0 
          ? formData.practices[0] 
          : formData.practice || '';
        
        await updateDoc(userRef, {
          ...userDataWithoutPassword,
          email: formData.email ? formData.email.toLowerCase().trim() : undefined,
          practice: primaryPractice, // Primary practice for backward compatibility
          practices: formData.practices || (formData.practice ? [formData.practice] : []), // All assigned practices
          userType: formData.userType || 'Office Staff',
          credentials: encryptedCredentials
        });
        
        // Log admin action for audit trail
        await logAdminAction(AUDIT_ACTIONS.USER_UPDATED, editingUserId, {
          updatedBy: userInfo.email
        });

        // Only check for expiring credentials if expiration dates within 3 months changed or were added
        if (formData.userType === 'Physician' && formData.credentials) {
          const changedCredentials = originalCredentials 
            ? getChangedExpiringCredentials(originalCredentials, formData.credentials)
            : null; // If no original credentials stored, check all (might be first time adding credentials)
          
          // Only send notification if there are changed credentials within 3 months, or if it's first time setup
          // If changedCredentials is an empty array, it means credentials changed but none are within 3 months - don't send email
          if (changedCredentials === null) {
            // First time setup - check all credentials
            await checkAndNotifyExpiringCredentials(
              formData, 
              `${formData.firstName || ''} ${formData.lastName || ''}`.trim() || formData.email, 
              true,
              null // Check all credentials
            );
          } else if (changedCredentials.length > 0) {
            // Only check the specific credentials that changed and are within 3 months
            await checkAndNotifyExpiringCredentials(
              formData, 
              `${formData.firstName || ''} ${formData.lastName || ''}`.trim() || formData.email, 
              true,
              changedCredentials // Pass the list of changed credentials to only check those
            );
          }
          // If changedCredentials.length === 0, do nothing (credentials changed but none are within 3 months)
        }
        
        // Clear original credentials after successful update
        setOriginalCredentials(null);
        
        // If user type is Physician, ensure they're in the physicians roster for all assigned practices
        if (formData.userType === 'Physician') {
          const hasAnyPractice = (formData.practices && formData.practices.length > 0) || formData.practice;
          if (hasAnyPractice) {
            await addPhysicianToRoster({ ...formData, uid: editingUserId });
          }
        }

        toast.success('User updated successfully!');

        setIsEditingUser(false);
        setIsAddingUser(false);
        setTimeout(() => {
          fetchUsers();
          fetchPhysicians(); // Refresh physicians list
        }, 200);
      }
    } catch (error) {
      console.error('Error saving user:', error);
      toast.error(toUserMessage(error, 'Unable to save that user. Please try again.'));
    }
  };

  const handleSendPasswordResetLink = async () => {
    if (!editingUserId || !formData.email) return;
    if (isSendingResetLinkRef.current) return; // Synchronous guard - blocks re-entrant calls before state can update
    isSendingResetLinkRef.current = true;
    setIsSendingResetLink(true);
    try {
      const sendPasswordResetFn = httpsCallable(functions, 'sendPasswordResetEmailForUser');
      await sendPasswordResetFn({
        userId: editingUserId,
        email: formData.email.toLowerCase().trim(),
        originUrl: window.location.origin // Pass tenant URL for correct reset link
      });
      toast.success('Password reset email sent to ' + formData.email);
    } catch (error) {
      console.error('Error sending password reset email:', error);
      toast.error(toUserMessage(error, 'Unable to send the reset email. Please try again.'));
    } finally {
      isSendingResetLinkRef.current = false;
      setIsSendingResetLink(false);
    }
  };

  const handleEditPractice = async (practiceId) => {
    try {
      const practiceRef = doc(db, 'practices', practiceId);
      const practiceDoc = await getDoc(practiceRef);

      if (practiceDoc.exists()) {
        const data = practiceDoc.data();
        const locs = getPracticeLocations(data);
        const locations = locs.length > 0 ? locs : [{ name: '', adminLead: data.adminLead || '', adminCell: data.officePhone || '', adminEmail: data.email || '' }];
        setFormData({
          ...data,
          locations
        });
        setEditingPracticeId(practiceId);
        setIsEditingPractice(true);
      }
    } catch (error) {
      console.error("Error fetching practice data: ", error);
    }
  };

  const handleDisablePractice = async (practiceId) => {
    try {
      const practiceRef = doc(db, 'practices', practiceId);
      const practiceDoc = await getDoc(practiceRef);
      const currentStatus = practiceDoc.data().status;
      
      await updateDoc(practiceRef, {
        status: currentStatus === 'Active' ? 'Inactive' : 'Active'
      });
      
      await fetchPractices();
      toast.success(`Practice ${currentStatus === 'Active' ? 'deactivated' : 'activated'} successfully!`);
    } catch (error) {
      toast.error(toUserMessage(error, 'Unable to update that practice. Please try again.'));
    }
  };

  const handleSubmitPractice = async (e) => {
    e.preventDefault();
    try {
      const locs = (formData.locations || []).filter(l => l.name || l.adminLead || l.adminCell || l.adminEmail);
      const locNames = locs.map(l => l.name).filter(Boolean);
      const practiceIdentifiers = [formData.practiceName, ...locNames.map(loc => `${formData.practiceName} (${loc})`)];
      const practicePayload = {
        practiceName: formData.practiceName,
        ...(locs.length > 0 && { officeLocations: locs }),
        practiceIdentifiers,
        organization: organization,
        officeAddress: formData.officeAddress || '',
        officeCity: formData.officeCity || '',
        officeState: formData.officeState || '',
        officeZipCode: formData.officeZipCode || '',
        officePhone: formData.officePhone || '',
        email: formData.email || '',
        status: formData.status || 'Active',
      };
      if (isAddingPractice) {
        await addDoc(collection(db, 'practices'), practicePayload);
        setIsAddingPractice(false);
        setIsEditingPractice(false);
        setTimeout(() => {
          toast.success('Practice created successfully!');
          fetchPractices();
        }, 200);
      } else {
        const practiceRef = doc(db, 'practices', editingPracticeId);
        await updateDoc(practiceRef, practicePayload);
        setIsEditingPractice(false);
        setTimeout(() => {
          toast.success('Practice updated successfully!');
          fetchPractices();
        }, 200);
      }
    } catch (error) {
      toast.error(toUserMessage(error, 'Unable to update that practice. Please try again.'));
    }
  };

  // Helper: practice identifier - always practiceName (locations are sub-items, not separate practices)
  const getPracticeIdentifier = (p) => {
    if (!p) return '';
    return p.practiceName || '';
  };

  // Get locations as normalized array of objects { name, adminLead, adminCell, adminEmail }
  // Handles: new object array, legacy string array, legacy single string
  const getPracticeLocations = (p) => {
    if (!p) return [];
    if (Array.isArray(p.officeLocations) && p.officeLocations.length) {
      return p.officeLocations.map(loc => {
        if (typeof loc === 'string') return { name: loc, adminLead: '', adminCell: '', adminEmail: '' };
        return { name: loc.name || '', adminLead: loc.adminLead || '', adminCell: loc.adminCell || '', adminEmail: loc.adminEmail || '' };
      });
    }
    if (p.officeLocation) {
      if (typeof p.officeLocation === 'string') return [{ name: p.officeLocation, adminLead: '', adminCell: '', adminEmail: '' }];
      return [p.officeLocation];
    }
    return [];
  };

  // Get location names only (for identifiers, matching, etc.)
  const getPracticeLocationNames = (p) => getPracticeLocations(p).map(l => l.name).filter(Boolean);

  const handleAddPractice = () => {
    setFormData({
      practiceName: '',
      locations: [{ name: '', adminLead: '', adminCell: '', adminEmail: '' }],
      officeAddress: '',
      officeCity: '',
      officeState: '',
      officeZipCode: '',
      officePhone: '',
      email: '',
      status: 'Active'
    });
    setEditingPracticeId(null);
    setIsAddingPractice(true);
    setIsEditingPractice(true);
  };

  // ============================================
  // PHYSICIAN ROSTER HANDLERS
  // ============================================
  
  // Show the choice dialog when clicking "Add Physician"
  const handleAddPhysician = () => {
    setShowPhysicianChoiceDialog(true);
  };

  // Option 1: Add to roster only (no account)
  const handleAddPhysicianToRosterOnly = () => {
    const targetPractice = userInfo.role === 'Practice Administrator'
      ? (activePractice || userInfo.practice)
      : selectedPractice || '';
    
    setPhysicianFormData({
      firstName: '',
      lastName: '',
      email: '', // Email is used to identify physicians across practices
      specialty: '',
      npi: '',
      practice: targetPractice, // Target practice for this addition
      status: 'Active'
    });
    setEditingPhysicianId(null);
    setIsAddingPhysician(true);
    setIsEditingPhysician(true);
    setShowPhysicianChoiceDialog(false);
  };

  // Option 2: Create account for physician (opens user form with userType locked to Physician)
  const handleCreatePhysicianAccount = () => {
    const targetPractice = userInfo.role === 'Practice Administrator' 
      ? (activePractice || userInfo.practice)
      : selectedPractice || '';
    
    // Initialize form with physician-specific settings
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      mobile: '',
      practice: targetPractice,
      practices: targetPractice ? [targetPractice] : [], // Support multiple practices
      role: 'Standard',
      status: 'Active',
      userType: 'Physician', // Locked to Physician
      credentials: initializePhysicianCredentials(),
      tempPassword: '',
      confirmTempPassword: ''
    });
    setIsCreatingPhysicianUser(true); // Lock userType selector
    setIsAddingUser(true);
    setShowPhysicianChoiceDialog(false);
  };

  const handleEditPhysician = (physicianId) => {
    const physician = physicians.find(p => p.id === physicianId);
    if (physician) {
      if (isPhysicianManagedInUsers(physician)) {
        toast.info('This physician is linked to a user account and must be edited in User Management.');
        return;
      }
      const targetPractice = userInfo.role === 'Practice Administrator'
        ? (activePractice || userInfo.practice)
        : selectedPractice || '';
      
      setPhysicianFormData({
        firstName: physician.firstName || '',
        lastName: physician.lastName || '',
        email: physician.email || '',
        specialty: physician.specialty || '',
        npi: physician.npi || '',
        practice: targetPractice,
        status: physician.statusForPractice || 'Active'
      });
      setEditingPhysicianId(physicianId);
      setIsAddingPhysician(false);
      setIsEditingPhysician(true);
    }
  };

  const handlePhysicianFormChange = (e) => {
    const { name, value } = e.target;
    setPhysicianFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmitPhysician = async (e) => {
    e.preventDefault();
    try {
      const targetPractice = physicianFormData.practice;
      
      if (isAddingPhysician) {
        // Check if a physician with this email already exists
        let existingPhysician = null;
        if (physicianFormData.email) {
          const physiciansCollection = collection(db, 'physicians');
          const q = query(physiciansCollection, where('email', '==', physicianFormData.email.toLowerCase()));
          const querySnapshot = await getDocs(q);
          if (!querySnapshot.empty) {
            existingPhysician = {
              id: querySnapshot.docs[0].id,
              ...querySnapshot.docs[0].data()
            };
          }
        }
        
        if (existingPhysician) {
          // Physician exists - add this practice to their practices array
          const physicianRef = doc(db, 'physicians', existingPhysician.id);
          let currentPractices = existingPhysician.practices || [];
          
          // Convert old format to new if necessary
          if (!Array.isArray(currentPractices) || currentPractices.length === 0) {
            if (existingPhysician.practice) {
              currentPractices = [{ 
                name: existingPhysician.practice, 
                status: existingPhysician.status || 'Active' 
              }];
            } else {
              currentPractices = [];
            }
          }
          
          // Check if already in this practice
          const existingPracticeIndex = currentPractices.findIndex(p => p.name === targetPractice);
          if (existingPracticeIndex >= 0) {
            toast.warning('This physician is already associated with this practice.');
            return;
          }
          
          // Add the new practice
          currentPractices.push({
            name: targetPractice,
            status: physicianFormData.status || 'Active'
          });
          
          await updateDoc(physicianRef, {
            practices: currentPractices,
            // Update basic info if provided (in case it changed)
            firstName: physicianFormData.firstName || existingPhysician.firstName,
            lastName: physicianFormData.lastName || existingPhysician.lastName,
            specialty: physicianFormData.specialty || existingPhysician.specialty,
            npi: physicianFormData.npi || existingPhysician.npi,
            updatedAt: new Date().toISOString(),
            updatedBy: userInfo.email
          });
          
          // Log the action
          await logAdminAction(AUDIT_ACTIONS.UPDATE, existingPhysician.id, {
            collection: 'physicians',
            action: 'added_to_practice',
            physicianName: `${physicianFormData.firstName} ${physicianFormData.lastName}`,
            practice: targetPractice
          });
          
          setIsAddingPhysician(false);
          setIsEditingPhysician(false);
          setTimeout(() => {
            toast.success(`Physician added to ${targetPractice}! They now belong to ${currentPractices.length} practice(s).`);
            fetchPhysicians();
          }, 200);
        } else {
          // New physician - create with practices array
          const newPhysicianData = {
            firstName: physicianFormData.firstName,
            lastName: physicianFormData.lastName,
            email: physicianFormData.email ? physicianFormData.email.toLowerCase() : '',
            specialty: physicianFormData.specialty || '',
            npi: physicianFormData.npi || '',
            practices: [{
              name: targetPractice,
              status: physicianFormData.status || 'Active'
            }],
            organization: organization, // Multi-tenancy support
            createdAt: new Date().toISOString(),
            createdBy: userInfo.email
          };

          const docRef = await addDoc(collection(db, 'physicians'), newPhysicianData);
          
          // Log the action
          await logAdminAction(AUDIT_ACTIONS.CREATE, docRef.id, {
            collection: 'physicians',
            physicianName: `${physicianFormData.firstName} ${physicianFormData.lastName}`,
            practice: targetPractice
          });
          
          setIsAddingPhysician(false);
          setIsEditingPhysician(false);
          setTimeout(() => {
            toast.success('Physician added to roster successfully!');
            fetchPhysicians();
          }, 200);
        }
      } else {
        // Editing existing physician
        const physicianRef = doc(db, 'physicians', editingPhysicianId);
        const physicianDoc = await getDoc(physicianRef);
        const existingData = physicianDoc.data();
        
        // Get or initialize practices array
        let currentPractices = existingData.practices || [];
        if (!Array.isArray(currentPractices) || currentPractices.length === 0) {
          if (existingData.practice) {
            currentPractices = [{ 
              name: existingData.practice, 
              status: existingData.status || 'Active' 
            }];
          } else {
            currentPractices = [];
          }
        }
        
        // Update status for the target practice
        const practiceIndex = currentPractices.findIndex(p => p.name === targetPractice);
        if (practiceIndex >= 0) {
          currentPractices[practiceIndex].status = physicianFormData.status;
        }
        
        await updateDoc(physicianRef, {
          firstName: physicianFormData.firstName,
          lastName: physicianFormData.lastName,
          email: physicianFormData.email ? physicianFormData.email.toLowerCase() : existingData.email || '',
          specialty: physicianFormData.specialty || '',
          npi: physicianFormData.npi || '',
          practices: currentPractices,
          updatedAt: new Date().toISOString(),
          updatedBy: userInfo.email
        });
        
        // Log the action
        await logAdminAction(AUDIT_ACTIONS.UPDATE, editingPhysicianId, {
          collection: 'physicians',
          physicianName: `${physicianFormData.firstName} ${physicianFormData.lastName}`
        });
        
        setIsEditingPhysician(false);
        setTimeout(() => {
          toast.success('Physician updated successfully!');
          fetchPhysicians();
        }, 200);
      }
    } catch (error) {
      toast.error(toUserMessage(error, 'Unable to save that physician. Please try again.'));
    }
  };

  const handleTogglePhysicianStatus = async (physicianId) => {
    try {
      const targetPractice = userInfo.role === 'Practice Administrator' 
        ? (activePractice || userInfo.practice)
        : selectedPractice || '';
      
      const physicianRef = doc(db, 'physicians', physicianId);
      const physicianDoc = await getDoc(physicianRef);
      const existingData = physicianDoc.data();
      
      // Get or initialize practices array
      let currentPractices = existingData.practices || [];
      if (!Array.isArray(currentPractices) || currentPractices.length === 0) {
        if (existingData.practice) {
          currentPractices = [{ 
            name: existingData.practice, 
            status: existingData.status || 'Active' 
          }];
        } else {
          currentPractices = [];
        }
      }
      
      // Find and toggle status for the target practice
      const practiceIndex = currentPractices.findIndex(p => p.name === targetPractice);
      if (practiceIndex >= 0) {
        const currentStatus = currentPractices[practiceIndex].status;
        currentPractices[practiceIndex].status = currentStatus === 'Active' ? 'Inactive' : 'Active';
        
        await updateDoc(physicianRef, {
          practices: currentPractices,
          updatedAt: new Date().toISOString(),
          updatedBy: userInfo.email
        });
        
        await fetchPhysicians();
        toast.success(`Physician ${currentStatus === 'Active' ? 'deactivated' : 'activated'} for ${targetPractice}!`);
      } else {
        toast.error('Physician not found for this practice');
      }
    } catch (error) {
      toast.error(toUserMessage(error, 'Unable to update that physician. Please try again.'));
    }
  };

  // Physicians linked to user accounts are managed from User Management.
  // Keep roster delete available only for roster-only records.
  const isPhysicianManagedInUsers = useCallback((physician) => {
    if (!physician) return false;
    if (physician.linkedUserId) return true;

    const physicianEmail = (physician.email || '').toLowerCase().trim();
    if (!physicianEmail) return false;

    return users.some(user =>
      (user.email || '').toLowerCase().trim() === physicianEmail
    );
  }, [users]);

  // Remove a physician from the roster (or just from the current practice if multi-practice)
  const handleDeletePhysician = async (physicianId) => {
    const physician = physicians.find(p => p.id === physicianId);
    if (!physician) return;
    if (isPhysicianManagedInUsers(physician)) {
      toast.info('This physician is linked to a user account and must be managed in User Management.');
      return;
    }

    const targetPractice = userInfo.role === 'Practice Administrator'
      ? (activePractice || userInfo.practice)
      : selectedPractice || '';

    const name = `${physician.firstName} ${physician.lastName}`;

    if (physician.practiceCount > 1 && targetPractice) {
      const confirmed = window.confirm(
        `This physician belongs to ${physician.practiceCount} practices. Remove "${name}" from ${targetPractice} only?\n\nClick OK to remove from this practice, or Cancel to abort.`
      );
      if (!confirmed) return;

      try {
        const physicianRef = doc(db, 'physicians', physicianId);
        const physicianDoc = await getDoc(physicianRef);
        const existingData = physicianDoc.data();
        let currentPractices = existingData.practices || [];
        currentPractices = currentPractices.filter(p => p.name !== targetPractice);

        await updateDoc(physicianRef, {
          practices: currentPractices,
          updatedAt: new Date().toISOString(),
          updatedBy: userInfo.email
        });

        await logAdminAction(AUDIT_ACTIONS.UPDATE || 'UPDATE', physicianId, {
          collection: 'physicians',
          action: 'removed_from_practice',
          physicianName: name,
          practice: targetPractice
        });

        await fetchPhysicians();
        toast.success(`"${name}" removed from ${targetPractice}.`);
      } catch (error) {
        toast.error(toUserMessage(error, 'Unable to remove that physician. Please try again.'));
      }
    } else {
      const confirmed = window.confirm(
        `This will permanently remove "${name}" from the physician roster. Are you sure?`
      );
      if (!confirmed) return;

      try {
        await deleteDoc(doc(db, 'physicians', physicianId));

        await logAdminAction(AUDIT_ACTIONS.DELETE || 'PHYSICIAN_DELETED', physicianId, {
          collection: 'physicians',
          physicianName: name,
          deletedBy: userInfo.email
        });

        await fetchPhysicians();
        toast.success(`"${name}" has been removed from the physician roster.`);
      } catch (error) {
        toast.error(toUserMessage(error, 'Unable to delete that physician. Please try again.'));
      }
    }
  };

  // Remove a deleted user's email from the physicians collection
  const removeUserFromPhysicianRoster = async (userEmail) => {
    if (!userEmail) return;
    const email = userEmail.toLowerCase().trim();
    try {
      const physiciansCollection = collection(db, 'physicians');
      const q = query(physiciansCollection, where('email', '==', email));
      const querySnapshot = await getDocs(q);
      for (const physicianDoc of querySnapshot.docs) {
        await deleteDoc(doc(db, 'physicians', physicianDoc.id));
      }
    } catch (error) {
      console.error('Error cleaning up physician roster for deleted user:', error);
    }
  };

  // Sync user status to linked physician roster entries (matched by email)
  const syncUserStatusToPhysicianRoster = async (userEmail, newStatus) => {
    if (!userEmail) return;
    const email = userEmail.toLowerCase().trim();
    try {
      const physiciansCollection = collection(db, 'physicians');
      const q = query(physiciansCollection, where('email', '==', email));
      const querySnapshot = await getDocs(q);
      for (const physicianDoc of querySnapshot.docs) {
        const data = physicianDoc.data();
        const physicianRef = doc(db, 'physicians', physicianDoc.id);
        if (data.practices && Array.isArray(data.practices)) {
          const updatedPractices = data.practices.map(p => ({
            ...p,
            status: newStatus
          }));
          await updateDoc(physicianRef, { practices: updatedPractices });
        } else {
          await updateDoc(physicianRef, { status: newStatus });
        }
      }
    } catch (error) {
      console.error('Error syncing user status to physician roster:', error);
    }
  };

  // Helper function to add a user to the physicians roster for multiple practices
  // Called automatically when creating/updating a user with userType "Physician"
  const addPhysicianToRoster = async (userData) => {
    try {
      const email = userData.email ? userData.email.toLowerCase().trim() : '';
      
      // Support both single practice and multiple practices
      let targetPractices = [];
      if (userData.practices && Array.isArray(userData.practices) && userData.practices.length > 0) {
        targetPractices = userData.practices;
      } else if (userData.practice) {
        targetPractices = [userData.practice];
      }
      
      if (targetPractices.length === 0) {
        console.warn('Cannot add physician to roster: no practices specified');
        return;
      }
      
      // Check if a physician with this email already exists
      let existingPhysician = null;
      if (email) {
        const physiciansCollection = collection(db, 'physicians');
        const q = query(physiciansCollection, where('email', '==', email));
        const querySnapshot = await getDocs(q);
        if (!querySnapshot.empty) {
          existingPhysician = {
            id: querySnapshot.docs[0].id,
            ...querySnapshot.docs[0].data()
          };
        }
      }
      
      if (existingPhysician) {
        // Physician exists - add new practices to their practices array if not already there
        const physicianRef = doc(db, 'physicians', existingPhysician.id);
        let currentPractices = existingPhysician.practices || [];
        
        // Convert old format to new if necessary
        if (!Array.isArray(currentPractices) || currentPractices.length === 0) {
          if (existingPhysician.practice) {
            currentPractices = [{ 
              name: existingPhysician.practice, 
              status: existingPhysician.status || 'Active' 
            }];
          } else {
            currentPractices = [];
          }
        }
        
        // Add each target practice if not already present
        let practicesAdded = [];
        for (const targetPractice of targetPractices) {
          const existingPracticeIndex = currentPractices.findIndex(p => p.name === targetPractice);
          if (existingPracticeIndex < 0) {
            currentPractices.push({
              name: targetPractice,
              status: 'Active'
            });
            practicesAdded.push(targetPractice);
          }
        }
        
        if (practicesAdded.length > 0) {
          await updateDoc(physicianRef, {
            practices: currentPractices,
            firstName: userData.firstName || existingPhysician.firstName,
            lastName: userData.lastName || existingPhysician.lastName,
            updatedAt: new Date().toISOString(),
            updatedBy: userInfo.email
          });
          console.log(`Added physician ${userData.firstName} ${userData.lastName} to practices: ${practicesAdded.join(', ')}`);
        }
      } else {
        // New physician - create with all practices
        const newPhysicianData = {
          firstName: userData.firstName || '',
          lastName: userData.lastName || '',
          email: email,
          specialty: userData.credentials?.specialty || '',
          npi: userData.credentials?.npiNumber || '',
          practices: targetPractices.map(p => ({
            name: p,
            status: 'Active'
          })),
          organization: organization, // Multi-tenancy support
          createdAt: new Date().toISOString(),
          createdBy: userInfo.email,
          linkedUserId: userData.uid || null // Link to the user account
        };
        
        await addDoc(collection(db, 'physicians'), newPhysicianData);
        console.log(`Created new physician roster entry for ${userData.firstName} ${userData.lastName} at practices: ${targetPractices.join(', ')}`);
      }
    } catch (error) {
      console.error('Error adding physician to roster:', error);
      // Don't throw - this is a secondary operation, user creation should still succeed
    }
  };

  // Download CSV template with Admin Location column for per-location contacts
  const downloadCSVTemplate = () => {
    const headers = ['Practice Name', 'Location', 'Admin/Lead', 'Admin Cell', 'Admin Email', 'Admin Location'];
    const exampleRows = [
      ['Memorial Oral Surgery', '"Cypress, Katy, San Felipe Houston"', 'Jackie Moreno', '(832) 692-0538', 'jackie@example.com', 'San Felipe Houston'],
      ['Memorial Oral Surgery', '"Cypress, Katy, San Felipe Houston"', 'Sarah Sellers', '(281) 961-4059', 'sarah@example.com', 'Cypress'],
      ['Memorial Oral Surgery', '"Cypress, Katy, San Felipe Houston"', 'Christina O\'Shell', '', 'christina@example.com', 'Katy'],
      ['Single Office Practice', '', 'Jane Doe', '(555) 123-4567', 'jane@example.com', '']
    ];
    
    const csvContent = [
      headers.join(','),
      ...exampleRows.map(r => r.join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'practice-upload-template.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('CSV template downloaded!');
  };

  // Parse CSV line
  const parseCSVLine = (line) => {
    const result = [];
    let current = '';
    let inQuotes = false;
    
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        result.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    
    result.push(current.trim());
    return result;
  };

  // Parse "Practice Name (Location)" format - returns { practiceName, location } or { practiceName } only
  const parsePracticeNameCell = (cell) => {
    if (!cell || typeof cell !== 'string') return { practiceName: '', location: null };
    const s = cell.trim();
    const match = s.match(/^(.+?)\s*\(([^)]+)\)\s*$/);
    if (match) {
      return { practiceName: match[1].trim(), location: match[2].trim() };
    }
    return { practiceName: s, location: null };
  };

  // Check if practice exists within the current organization (by practiceName or email)
  const practiceExists = async (practiceName, email) => {
    try {
      const practicesRef = collection(db, 'practices');
      
      const nameQuery = query(practicesRef, where('practiceName', '==', practiceName), where('organization', '==', organization));
      const nameSnapshot = await getDocs(nameQuery);
      if (!nameSnapshot.empty) {
        return { exists: true, reason: 'practiceName', doc: nameSnapshot.docs[0] };
      }
      
      if (email && email.trim()) {
        const emailQuery = query(practicesRef, where('email', '==', email.toLowerCase().trim()), where('organization', '==', organization));
        const emailSnapshot = await getDocs(emailQuery);
        if (!emailSnapshot.empty) return { exists: true, reason: 'email' };
      }
      
      return { exists: false };
    } catch (error) {
      console.error('Error checking practice existence:', error);
      return { exists: false };
    }
  };

  // Escape CSV value (handle commas, quotes, newlines)
  const escapeCSVValue = (val) => {
    if (val == null || val === '') return '';
    const s = String(val);
    if (s.includes(',') || s.includes('"') || s.includes('\n')) {
      return '"' + s.replace(/"/g, '""') + '"';
    }
    return s;
  };

  // Export current practices: one row per location-contact (multi-location practices get multiple rows)
  const exportPracticesCSV = () => {
    const headers = ['Practice Name', 'Location', 'Admin/Lead', 'Admin Cell', 'Admin Email', 'Admin Location'];
    const rows = [];
    const byName = {};
    for (const p of practices) {
      const name = (p.practiceName || '').trim();
      if (!name) continue;
      if (!byName[name]) {
        const locs = getPracticeLocations(p);
        byName[name] = { ...p, _locs: locs };
      } else {
        for (const loc of getPracticeLocations(p)) {
          if (!byName[name]._locs.find(l => l.name === loc.name)) byName[name]._locs.push(loc);
        }
      }
    }
    for (const p of Object.values(byName)) {
      const locNames = (p._locs || []).filter(l => l.name).map(l => l.name);
      const locationStr = locNames.join(', ');
      if (p._locs.length > 0 && p._locs.some(l => l.name)) {
        for (const loc of p._locs.filter(l => l.name)) {
          rows.push([
            escapeCSVValue(p.practiceName),
            escapeCSVValue(locationStr),
            escapeCSVValue(loc.adminLead || p.adminLead || ''),
            escapeCSVValue(loc.adminCell || p.officePhone || ''),
            escapeCSVValue(loc.adminEmail || p.email || ''),
            escapeCSVValue(loc.name)
          ].join(','));
        }
      } else {
        rows.push([
          escapeCSVValue(p.practiceName),
          escapeCSVValue(locationStr),
          escapeCSVValue(p.adminLead || ''),
          escapeCSVValue(p.officePhone || ''),
          escapeCSVValue(p.email || ''),
          ''
        ].join(','));
      }
    }
    const csvContent = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `practices-export-${new Date().toISOString().slice(0, 10)}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success(`Exported ${Object.keys(byName).length} practice(s).`);
  };

  // Export current users list in bulk upload template format (credentials decrypted)
  const exportUsersCSV = async () => {
    setIsExportingUsers(true);
    try {
    const headers = [
      'First Name', 'Last Name', 'Email', 'Temporary Password', 'Mobile', 'Practices', 'Role', 'Status', 'User Type',
      'Medical License Number', 'Medical License Expiration', 'Dental License Number', 'Dental License Expiration',
      'DEA Number', 'DEA Expiration', 'CME Credit Hours', 'Malpractice Expiration'
    ];
    const rows = [];
    for (const u of users) {
      let creds = u.credentials || {};
      if (creds && typeof creds.medicalLicense?.number === 'string' && creds.medicalLicense.number.startsWith('ENC:')) {
        try {
          creds = await decryptCredentials(creds);
        } catch {
          creds = u.credentials || {};
        }
      }
      const practicesStr = Array.isArray(u.practices) ? u.practices.join(';') : (u.practice || '');
      const formatExpDate = (d) => {
        if (!d) return '';
        const m = d.match(/^(\d{4})-(\d{2})-(\d{2})/);
        return m ? `${m[2]}/${m[3]}/${m[1]}` : d;
      };
      const row = [
        escapeCSVValue(u.firstName || ''),
        escapeCSVValue(u.lastName || ''),
        escapeCSVValue(u.email || ''),
        '', // No password for existing users
        escapeCSVValue(u.mobile || ''),
        escapeCSVValue(practicesStr),
        escapeCSVValue(u.role || 'Standard'),
        escapeCSVValue(u.status || 'Active'),
        escapeCSVValue(u.userType || 'Office Staff'),
        escapeCSVValue(creds.medicalLicense?.number || ''),
        escapeCSVValue(formatExpDate(creds.medicalLicense?.expirationDate || '')),
        escapeCSVValue(creds.dentalLicense?.number || ''),
        escapeCSVValue(formatExpDate(creds.dentalLicense?.expirationDate || '')),
        escapeCSVValue(creds.deaCertification?.number || ''),
        escapeCSVValue(formatExpDate(creds.deaCertification?.expirationDate || '')),
        escapeCSVValue(creds.cme?.creditHours || ''),
        escapeCSVValue(formatExpDate(creds.malpracticeInsurance?.expirationDate || ''))
      ].join(',');
      rows.push(row);
    }
    const csvContent = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `users-export-${new Date().toISOString().slice(0, 10)}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success(`Exported ${users.length} user(s).`);
  } catch (error) {
    console.error('Export users error:', error);
    toast.error('Failed to export users.');
  } finally {
    setIsExportingUsers(false);
  }
  };

  // Download user bulk upload CSV template (Excel-compatible: save as CSV)
  const downloadUserCSVTemplate = () => {
    const headers = [
      'First Name',
      'Last Name',
      'Email',
      'Temporary Password',
      'Mobile',
      'Practices',
      'Role',
      'Status',
      'User Type',
      'Medical License Number',
      'Medical License Expiration',
      'Dental License Number',
      'Dental License Expiration',
      'DEA Number',
      'DEA Expiration',
      'CME Credit Hours',
      'Malpractice Expiration'
    ];
    const exampleRow = [
      'John',
      'Doe',
      'john.doe@example.com',
      'TempPass123!',
      '(555) 123-4567',
      'North Austin Oral Surgery',
      'Standard',
      'Active',
      'Office Staff',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      ''
    ];
    const exampleRow2 = [
      'Jane',
      'Smith',
      'jane.smith@example.com',
      'SecurePass456!',
      '',
      'Practice A;Practice B',
      'Practice Administrator',
      'Pending',
      'Physician',
      'ML12345',
      '2026-12-31',
      'DL67890',
      '2026-06-30',
      'AS1234567',
      '2026-03-15',
      '50',
      '2026-01-01'
    ];
    const csvContent = [
      headers.join(','),
      exampleRow.join(','),
      exampleRow2.join(',')
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'user-upload-template.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success('User CSV template downloaded! Save as CSV from Excel if needed.');
  };

  // Handle user CSV bulk upload
  const handleUserCSVUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const isCsv = file.name.toLowerCase().endsWith('.csv');
    if (!isCsv) {
      toast.error('Please upload a CSV file (Excel: Save As > CSV)');
      return;
    }

    setIsBulkUploadingUsers(true);
    setBulkUserUploadProgress({ success: 0, skipped: 0, errors: 0 });

    try {
      const text = await file.text();
      const lines = text.split('\n').filter(line => line.trim());

      if (lines.length < 2) {
        toast.error('CSV file must have at least a header row and one data row');
        setIsBulkUploadingUsers(false);
        return;
      }

      const headers = parseCSVLine(lines[0]);
      const requiredHeaders = [
        'First Name',
        'Last Name',
        'Email',
        'Temporary Password',
        'Practices',
        'Role',
        'Status'
      ];

      const hasAllHeaders = requiredHeaders.every(header =>
        headers.some(h => h.trim() === header)
      );

      if (!hasAllHeaders) {
        toast.error('CSV headers do not match. Required: First Name, Last Name, Email, Temporary Password, Practices, Role, Status. Download the template.');
        setIsBulkUploadingUsers(false);
        return;
      }

      let successCount = 0;
      let skipCount = 0;
      let errorCount = 0;

      const createUserFn = httpsCallable(functions, 'createUser');
      const activePractices = practices.filter(p => p.status === 'Active').map(p => p.practiceName);

      for (let i = 1; i < lines.length; i++) {
        try {
          let values = parseCSVLine(lines[i]);
          if (values.length < headers.length) {
            values = [...values, ...Array(headers.length - values.length).fill('')];
          }

          const row = {};
          headers.forEach((header, index) => {
            row[header.trim()] = (values[index] || '').trim();
          });

          const firstName = row['First Name'] || '';
          const lastName = row['Last Name'] || '';
          const email = (row['Email'] || '').toLowerCase();
          const tempPassword = row['Temporary Password'] || '';
          const mobile = row['Mobile'] || '';
          const practicesStr = row['Practices'] || '';
          const role = row['Role'] || 'Standard';
          const status = row['Status'] || 'Active';
          const userType = row['User Type'] || 'Office Staff';

          const medicalLicenseNum = row['Medical License Number'] || '';
          const medicalLicenseExp = row['Medical License Expiration'] || '';
          const dentalLicenseNum = row['Dental License Number'] || '';
          const dentalLicenseExp = row['Dental License Expiration'] || '';
          const deaNum = row['DEA Number'] || '';
          const deaExp = row['DEA Expiration'] || '';
          const cmeHours = row['CME Credit Hours'] || '';
          const malpracticeExp = row['Malpractice Expiration'] || '';

          if (!firstName || !lastName || !email || !tempPassword) {
            skipCount++;
            continue;
          }

          if (tempPassword.length < 6) {
            toast.error(`Row ${i + 1}: Password must be at least 6 characters`);
            errorCount++;
            continue;
          }

          const practiceList = practicesStr
            .split(/[;,|]/)
            .map(p => p.trim())
            .filter(p => p);

          if (practiceList.length === 0 && role !== 'System Administrator') {
            skipCount++;
            continue;
          }

          let targetPractices = practiceList;
          if (userInfo.role === 'Practice Administrator') {
            const userPractice = userInfo.practice || userInfo.practices?.[0];
            if (!practiceList.includes(userPractice)) {
              skipCount++;
              continue;
            }
            targetPractices = [userPractice];
          } else {
            const invalidPractices = practiceList.filter(p => !activePractices.includes(p));
            if (invalidPractices.length > 0) {
              toast.warning(`Row ${i + 1} skipped: Practice "${invalidPractices.join(', ')}" not found. Use exact names from Practice Management (e.g. ${activePractices.slice(0, 3).join(', ')}${activePractices.length > 3 ? '...' : ''}).`);
              skipCount++;
              setBulkUserUploadProgress({ success: successCount, skipped: skipCount, errors: errorCount });
              continue;
            }
            targetPractices = practiceList;
          }

          if (!roles.includes(role) || !statuses.includes(status)) {
            skipCount++;
            continue;
          }

          const validUserTypes = ['Office Staff', 'Physician'];
          const finalUserType = validUserTypes.includes(userType) ? userType : 'Office Staff';

          const normalizeDate = (val) => {
            if (!val || !val.trim()) return '';
            const v = val.trim();
            const mdy = v.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{2,4})$/);
            if (mdy) {
              const y = mdy[3].length === 2 ? '20' + mdy[3] : mdy[3];
              const m = mdy[1].padStart(2, '0');
              const d = mdy[2].padStart(2, '0');
              return `${y}-${m}-${d}`;
            }
            if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return v;
            return v;
          };

          const credentials = initializePhysicianCredentials();
          if (finalUserType === 'Physician') {
            if (medicalLicenseNum) credentials.medicalLicense.number = medicalLicenseNum;
            if (medicalLicenseExp) credentials.medicalLicense.expirationDate = normalizeDate(medicalLicenseExp);
            if (dentalLicenseNum) credentials.dentalLicense.number = dentalLicenseNum;
            if (dentalLicenseExp) credentials.dentalLicense.expirationDate = normalizeDate(dentalLicenseExp);
            if (deaNum) credentials.deaCertification.number = deaNum;
            if (deaExp) credentials.deaCertification.expirationDate = normalizeDate(deaExp);
            if (cmeHours) credentials.cme.creditHours = cmeHours;
            if (malpracticeExp) credentials.malpracticeInsurance.expirationDate = normalizeDate(malpracticeExp);
          }

          try {
            const result = await createUserFn({
              email,
              password: tempPassword,
              userData: {
                practice: targetPractices[0] || '',
                practices: targetPractices,
                organization
              }
            });
            const authUserUid = result.data.uid;

            const credentialsToSave = finalUserType === 'Physician'
              ? await encryptCredentials(credentials)
              : {};

            const userData = {
              firstName,
              lastName,
              email,
              mobile: mobile ? formatPhoneNumber(mobile) : '',
              practice: targetPractices[0] || '',
              practices: targetPractices,
              role,
              status,
              userType: finalUserType,
              organization,
              credentials: credentialsToSave,
              createdAt: new Date(),
              createdBy: userInfo.email,
              requiresPasswordChange: true // First login must change temp password
            };

            await setDoc(doc(db, 'users', authUserUid), userData);
            await logAdminAction(AUDIT_ACTIONS.USER_CREATED, authUserUid, {
              createdBy: userInfo.email,
              source: 'bulk_upload'
            });

            if (finalUserType === 'Physician' && targetPractices.length > 0) {
              await addPhysicianToRoster({ ...userData, uid: authUserUid });
            }

            successCount++;
            setBulkUserUploadProgress({ success: successCount, skipped: skipCount, errors: errorCount });
          } catch (createError) {
            console.error(`Error creating user row ${i + 1}:`, createError);
            if (createError.code === 'functions/already-exists') {
              toast.info(`Row ${i + 1} (${email}) skipped: Already registered.`);
              skipCount++;
            } else {
              errorCount++;
            }
            setBulkUserUploadProgress({ success: successCount, skipped: skipCount, errors: errorCount });
          }
        } catch (rowError) {
          console.error(`Error processing row ${i + 1}:`, rowError);
          errorCount++;
          setBulkUserUploadProgress({ success: successCount, skipped: skipCount, errors: errorCount });
        }
      }

      await fetchUsers();
      await fetchPhysicians();

      const totalRows = lines.length - 1;
      toast.success(`Bulk user upload complete! ${successCount} created, ${skipCount} skipped, ${errorCount} errors (${totalRows} rows in file)`);
      setIsBulkUploadingUsers(false);
      setBulkUserUploadProgress({ success: 0, skipped: 0, errors: 0 });
      event.target.value = '';
    } catch (error) {
      console.error('Error processing user CSV:', error);
      toast.error(toUserMessage(error, 'Unable to process that CSV file. Please try again.'));
      setIsBulkUploadingUsers(false);
      event.target.value = '';
    }
  };

  // Handle CSV file upload
  const handleCSVUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      toast.error('Please upload a CSV file');
      return;
    }

    setIsBulkUploading(true);
    setBulkUploadProgress({ success: 0, skipped: 0, errors: 0 });

    try {
      const text = await file.text();
      const lines = text.split('\n').filter(line => line.trim());
      
      if (lines.length < 2) {
        toast.error('CSV file must have at least a header row and one data row');
        setIsBulkUploading(false);
        return;
      }

      const headers = parseCSVLine(lines[0]);
      const requiredHeaders = ['Practice Name'];

      const hasAllHeaders = requiredHeaders.every(header =>
        headers.some(h => h.trim() === header)
      );

      if (!hasAllHeaders) {
        toast.error('CSV must include a "Practice Name" column. Please download the template.');
        setIsBulkUploading(false);
        return;
      }

      // Phase 1: Parse all rows and group by practice name
      const practiceGroups = {};
      for (let i = 1; i < lines.length; i++) {
        const values = parseCSVLine(lines[i]);
        const row = {};
        headers.forEach((header, index) => { row[header.trim()] = (values[index] || '').trim(); });

        const nameCell = row['Practice Name'] || '';
        const { practiceName, location: locFromParens } = parsePracticeNameCell(nameCell);
        if (!practiceName) continue;

        const locationCell = row['Location'] || '';
        const adminLead = row['Admin/Lead'] || '';
        const adminCell = row['Admin Cell'] || '';
        const adminEmail = (row['Admin Email'] || '').toLowerCase();
        const adminLocation = row['Admin Location'] || '';

        // Collect location names from the Location column + parens in name
        const locsFromCol = locationCell ? locationCell.split(',').map(s => s.trim()).filter(Boolean) : [];
        const allLocationNames = [...new Set([...(locFromParens ? [locFromParens] : []), ...locsFromCol])];

        if (!practiceGroups[practiceName]) {
          practiceGroups[practiceName] = { locationNames: new Set(), contacts: [], rows: [] };
        }
        const group = practiceGroups[practiceName];
        allLocationNames.forEach(n => group.locationNames.add(n));
        if (adminLead || adminCell || adminEmail) {
          group.contacts.push({ adminLead, adminCell, adminEmail, adminLocation });
        }
        group.rows.push(row);
      }

      // Phase 2: Build and save each practice
      let successCount = 0;
      let skipCount = 0;
      let errorCount = 0;

      for (const [practiceName, group] of Object.entries(practiceGroups)) {
        try {
          const locationNames = [...group.locationNames];

          // Build officeLocations as array of objects with per-location contacts
          const officeLocations = locationNames.map(locName => {
            const contact = group.contacts.find(c => c.adminLocation && c.adminLocation.toLowerCase() === locName.toLowerCase());
            return {
              name: locName,
              adminLead: contact ? contact.adminLead : '',
              adminCell: contact ? contact.adminCell : '',
              adminEmail: contact ? contact.adminEmail : ''
            };
          });

          // Practice-level admin: first contact without an adminLocation, or first contact overall
          const practiceAdmin = group.contacts.find(c => !c.adminLocation) || group.contacts[0] || {};
          const practiceIdentifiers = [practiceName, ...locationNames.map(loc => `${practiceName} (${loc})`)];

          const exists = await practiceExists(practiceName, null);

          if (exists.exists && exists.reason === 'practiceName' && exists.doc) {
            // Merge with existing practice
            const existing = exists.doc.data();
            const existingLocs = getPracticeLocations(existing);
            const mergedMap = {};
            for (const loc of existingLocs) mergedMap[loc.name || ''] = { ...loc };
            for (const loc of officeLocations) {
              if (mergedMap[loc.name]) {
                if (loc.adminLead) mergedMap[loc.name].adminLead = loc.adminLead;
                if (loc.adminCell) mergedMap[loc.name].adminCell = loc.adminCell;
                if (loc.adminEmail) mergedMap[loc.name].adminEmail = loc.adminEmail;
              } else {
                mergedMap[loc.name] = loc;
              }
            }
            const mergedLocs = Object.values(mergedMap).filter(l => l.name);
            const mergedNames = mergedLocs.map(l => l.name);
            const mergedIdentifiers = [practiceName, ...mergedNames.map(n => `${practiceName} (${n})`)];

            const updatePayload = {
              ...(mergedLocs.length > 0 && { officeLocations: mergedLocs }),
              practiceIdentifiers: mergedIdentifiers,
              adminLead: practiceAdmin.adminLead || existing.adminLead || '',
              officePhone: practiceAdmin.adminCell || existing.officePhone || '',
              email: practiceAdmin.adminEmail || existing.email || ''
            };
            await updateDoc(doc(db, 'practices', exists.doc.id), updatePayload);
          } else {
            const practiceData = {
              practiceName,
              ...(officeLocations.length > 0 && { officeLocations }),
              practiceIdentifiers,
              organization,
              officeAddress: '',
              officeCity: '',
              officeState: '',
              officeZipCode: '',
              officePhone: practiceAdmin.adminCell || '',
              email: practiceAdmin.adminEmail || '',
              status: 'Active',
              adminLead: practiceAdmin.adminLead || '',
              createdAt: new Date().toISOString()
            };
            await addDoc(collection(db, 'practices'), practiceData);
          }
          successCount++;
          setBulkUploadProgress({ success: successCount, skipped: skipCount, errors: errorCount });
        } catch (error) {
          console.error(`Error processing practice "${practiceName}":`, error);
          errorCount++;
          setBulkUploadProgress({ success: successCount, skipped: skipCount, errors: errorCount });
        }
      }

      await fetchPractices();

      toast.success(`Bulk upload complete! ${successCount} uploaded, ${skipCount} skipped, ${errorCount} errors`);
      setIsBulkUploading(false);
      setBulkUploadProgress({ success: 0, skipped: 0, errors: 0 });

      event.target.value = '';

    } catch (error) {
      console.error('Error processing CSV:', error);
      toast.error(toUserMessage(error, 'Unable to process that CSV file. Please try again.'));
      setIsBulkUploading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    let formattedValue = value;
    
    if (name === 'mobile' || name === 'officePhone') {
      formattedValue = formatPhoneNumber(value);
    }
    
    // Handle profile editing (both inline and modal)
    if (isEditingProfile) {
      setProfileData({ ...profileData, [name]: formattedValue });
    } else {
      if (name === 'role' && value === 'System Administrator') {
        setFormData({ ...formData, [name]: formattedValue, practice: '' });
      } else if (type === 'radio') {
        setFormData({ ...formData, [name]: value });
      } else {
        setFormData({ ...formData, [name]: formattedValue });
      }
    }
  };

  // Initialize physician credentials structure
  const initializePhysicianCredentials = () => {
    return {
      medicalLicense: {
        number: '',
        expirationDate: ''
      },
      dentalLicense: {
        number: '',
        expirationDate: ''
      },
      deaCertification: {
        number: '',
        expirationDate: ''
      },
      cme: {
        creditHours: '',
        certificates: [] // Array of { url, fileName, uploadedAt }
      },
      malpracticeInsurance: {
        expirationDate: ''
      }
    };
  };

  const handleCredentialChange = (path, value) => {
    setFormData(prev => {
      const newCredentials = { ...(prev.credentials || initializePhysicianCredentials()) };
      const keys = path.split('.');
      let current = newCredentials;
      
      for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]]) {
          current[keys[i]] = {};
        }
        current = current[keys[i]];
      }
      
      current[keys[keys.length - 1]] = value;
      
      return {
        ...prev,
        credentials: newCredentials
      };
    });
  };

  const handleProfileCredentialChange = (path, value) => {
    setProfileData(prev => {
      const newCredentials = { ...(prev.credentials || initializePhysicianCredentials()) };
      const keys = path.split('.');
      let current = newCredentials;
      
      for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]]) {
          current[keys[i]] = {};
        }
        current = current[keys[i]];
      }
      
      current[keys[keys.length - 1]] = value;
      
      return {
        ...prev,
        credentials: newCredentials
      };
    });
  };

  // Upload CME certificate image
  const handleCMEImageUpload = async (file, isProfile = false) => {
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) {
        toast.error('User not authenticated');
        return;
      }

      // Ensure user is authenticated before upload
      await user.getIdToken(true); // Refresh token to ensure it's valid

      const timestamp = Date.now();
      // Sanitize filename to remove any special characters that might cause issues
      const sanitizedFileName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      const fileName = `cme-certificates/${user.uid}/${timestamp}_${sanitizedFileName}`;
      const storageRef = ref(storage, fileName);
      
      console.log('Uploading CME certificate to:', fileName);
      console.log('User UID:', user.uid);
      
      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef);

      const certificateData = {
        url: downloadURL,
        fileName: file.name,
        uploadedAt: new Date().toISOString()
      };

      if (isProfile) {
        setProfileData(prev => ({
          ...prev,
          credentials: {
            ...(prev.credentials || initializePhysicianCredentials()),
            cme: {
              ...(prev.credentials?.cme || { creditHours: '', certificates: [] }),
              certificates: [...(prev.credentials?.cme?.certificates || []), certificateData]
            }
          }
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          credentials: {
            ...(prev.credentials || initializePhysicianCredentials()),
            cme: {
              ...(prev.credentials?.cme || { creditHours: '', certificates: [] }),
              certificates: [...(prev.credentials?.cme?.certificates || []), certificateData]
            }
          }
        }));
      }

      toast.success('CME certificate uploaded successfully');
    } catch (error) {
      console.error('Error uploading CME certificate:', error);
      console.error('Error code:', error.code);
      console.error('Error details:', error);
      
      // Provide more specific error messages
      if (error.code === 'storage/unauthorized') {
        toast.error('Permission denied. Please ensure you are logged in and have permission to upload CME certificates.');
      } else if (error.code === 'storage/canceled') {
        toast.error('Upload was canceled.');
      } else if (error.code === 'storage/unknown') {
        toast.error('An unknown error occurred during upload. Please try again.');
      } else {
        toast.error(toUserMessage(error, 'Unable to upload that certificate. Please try again.'));
      }
    }
  };

  // Remove CME certificate image
  const handleRemoveCMECertificate = async (index, url, isProfile = false) => {
    try {
      // Delete from storage - extract path from URL
      if (url) {
        try {
          // Extract the path from the download URL
          // URL format: https://firebasestorage.googleapis.com/v0/b/{bucket}/o/{path}?alt=media
          const urlObj = new URL(url);
          const pathMatch = urlObj.pathname.match(/\/o\/(.+)\?/);
          if (pathMatch) {
            const filePath = decodeURIComponent(pathMatch[1]);
            const storageRef = ref(storage, filePath);
            await deleteObject(storageRef);
          }
        } catch (deleteError) {
          console.error('Error deleting file from storage:', deleteError);
          // Continue even if deletion fails
        }
      }

      if (isProfile) {
        setProfileData(prev => ({
          ...prev,
          credentials: {
            ...(prev.credentials || initializePhysicianCredentials()),
            cme: {
              ...(prev.credentials?.cme || { creditHours: '', certificates: [] }),
              certificates: (prev.credentials?.cme?.certificates || []).filter((_, i) => i !== index)
            }
          }
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          credentials: {
            ...(prev.credentials || initializePhysicianCredentials()),
            cme: {
              ...(prev.credentials?.cme || { creditHours: '', certificates: [] }),
              certificates: (prev.credentials?.cme?.certificates || []).filter((_, i) => i !== index)
            }
          }
        }));
      }

      toast.success('CME certificate removed');
    } catch (error) {
      console.error('Error removing CME certificate:', error);
      toast.error('Failed to remove CME certificate');
    }
  };

  // Helper function to determine which notification window applies (6, 3, or 1 month)
  const getNotificationWindow = (daysUntilExpiration) => {
    if (daysUntilExpiration <= 30) {
      return { window: '1 month', priority: 'high' };
    } else if (daysUntilExpiration <= 90) {
      return { window: '3 months', priority: 'medium' };
    } else if (daysUntilExpiration <= 180) {
      return { window: '6 months', priority: 'low' };
    }
    return null;
  };

  // Helper function to check and notify about expiring credentials for a single user
  // immediateCheck: if true, only check for 3 months (for immediate notifications on create/update)
  // onlyCheckCredentials: optional array of credential keys to check (e.g., ['medicalLicense', 'dentalLicense'])
  const checkAndNotifyExpiringCredentials = async (userData, userName, immediateCheck = false, onlyCheckCredentials = null) => {
    try {
      const sixMonthsFromNow = new Date();
      sixMonthsFromNow.setMonth(sixMonthsFromNow.getMonth() + 6);
      const threeMonthsFromNow = new Date();
      threeMonthsFromNow.setMonth(threeMonthsFromNow.getMonth() + 3);
      const oneMonthFromNow = new Date();
      oneMonthFromNow.setMonth(oneMonthFromNow.getMonth() + 1);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const expiring = [];
      if (userData.credentials && typeof userData.credentials === 'object') {
        const creds = userData.credentials;
        
        const checkCredential = (credentialKey, credentialName, expirationDate) => {
          if (!expirationDate) return;
          
          // If onlyCheckCredentials is specified (and not null), only check those credentials
          // If it's an empty array, check nothing (return early for all)
          if (onlyCheckCredentials !== null) {
            if (!onlyCheckCredentials.includes(credentialKey)) {
              return;
            }
          }
          
          const expDate = new Date(expirationDate);
          expDate.setHours(0, 0, 0, 0);
          const daysUntilExpiration = Math.ceil((expDate - today) / (1000 * 60 * 60 * 24));
          
          // Skip if already expired
          if (expDate < today) return;
          
          // For immediate checks (on create/update), only notify if within 3 months
          if (immediateCheck) {
            if (expDate <= threeMonthsFromNow && expDate >= today) {
              const notificationWindow = getNotificationWindow(daysUntilExpiration);
              expiring.push({
                userName,
                practice: userData.practice || 'N/A',
                credentialName,
                expirationDate,
                daysUntilExpiration,
                notificationWindow: notificationWindow?.window || '3 months',
                priority: notificationWindow?.priority || 'medium'
              });
            }
          } else {
            // For scheduled checks, only notify at exact thresholds (6 months, 3 months, 1 month)
            // Use a small window (±1 day) to account for timing variations in daily checks
            // This ensures notifications are sent only once per threshold, not every day
            const isAtSixMonthThreshold = daysUntilExpiration >= 179 && daysUntilExpiration <= 181; // ~180 days (6 months)
            const isAtThreeMonthThreshold = daysUntilExpiration >= 89 && daysUntilExpiration <= 91; // ~90 days (3 months)
            const isAtOneMonthThreshold = daysUntilExpiration >= 29 && daysUntilExpiration <= 31; // ~30 days (1 month)
            
            // Only trigger if at one of the exact thresholds (not in between)
            if (isAtSixMonthThreshold || isAtThreeMonthThreshold || isAtOneMonthThreshold) {
              const notificationWindow = getNotificationWindow(daysUntilExpiration);
              if (notificationWindow) {
                expiring.push({
                  userName,
                  practice: userData.practice || 'N/A',
                  credentialName,
                  expirationDate,
                  daysUntilExpiration,
                  notificationWindow: notificationWindow.window,
                  priority: notificationWindow.priority
                });
              }
            }
          }
        };
        
        // Check all credentials (pass credential key for filtering)
        checkCredential('medicalLicense', 'Medical License', creds.medicalLicense?.expirationDate);
        checkCredential('dentalLicense', 'Dental License', creds.dentalLicense?.expirationDate);
        checkCredential('deaCertification', 'DEA Certification', creds.deaCertification?.expirationDate);
        checkCredential('malpracticeInsurance', 'Malpractice Insurance', creds.malpracticeInsurance?.expirationDate);
      }

      if (expiring.length > 0) {
        // Group by notification window
        const byWindow = {
          '1 month': expiring.filter(e => e.notificationWindow === '1 month'),
          '3 months': expiring.filter(e => e.notificationWindow === '3 months'),
          '6 months': expiring.filter(e => e.notificationWindow === '6 months')
        };

        // Send separate emails for each window that has expiring credentials
        for (const [window, items] of Object.entries(byWindow)) {
          if (items.length > 0) {
            const emailContent = items.map(item => 
              `- ${item.userName} (${item.practice}): ${item.credentialName} expires in ${item.daysUntilExpiration} days (${formatDateString(item.expirationDate)})`
            ).join('\n');

            const subject = immediateCheck 
              ? `Physician Credential Expiring Within 3 Months - ${userName}`
              : `Physician Credential Expiring Within ${window} - ${userName}`;

            await queueEmail('operations', {
              type: 'credential_expiration',
              expiringCredentials: items,
              notificationWindow: window
            }, {
              subject,
              text: `The following physician credential(s) are expiring within ${window}:\n\n${emailContent}\n\nPlease review and take appropriate action.`,
              html: `
                <div style="font-family: Arial, sans-serif; padding: 20px;">
                  <h2>Physician Credential Expiring Within ${window}</h2>
                  <p><strong>Physician:</strong> ${userName}</p>
                  <p><strong>Practice:</strong> ${userData.practice || 'N/A'}</p>
                  <div style="background-color: #f5f5f5; padding: 15px; border-radius: 5px; margin-top: 20px;">
                    <h3>Expiring Credentials:</h3>
                    <ul style="list-style-type: none; padding: 0;">
                      ${items.map(item => `
                        <li style="padding: 8px 0; border-bottom: 1px solid #ddd;">
                          <strong>${item.credentialName}</strong><br>
                          <span style="color: ${item.priority === 'high' ? '#d32f2f' : item.priority === 'medium' ? '#f57c00' : '#f9a825'};">
                            Expires in ${item.daysUntilExpiration} days (${formatDateString(item.expirationDate)})
                          </span>
                        </li>
                      `).join('')}
                    </ul>
                  </div>
                  <p style="margin-top: 20px; color: #666; font-size: 12px;">
                    ${immediateCheck ? 'This notification was triggered because credentials were recently created or updated.' : 'This is an automated scheduled notification.'} Please review and take appropriate action.
                  </p>
                </div>
              `
            });
          }
        }
      }
    } catch (error) {
      console.error('Error checking expiring credentials for user:', error);
      // Don't fail user creation/update if email fails
    }
  };

  // Check for expiring credentials and send email notifications (scheduled check for 6, 3, 1 month windows)
  const checkExpiringCredentials = useCallback(async () => {
    setIsCheckingCredentials(true);
    try {
      const usersCollection = collection(db, 'users');
      const userSnapshot = await getDocs(usersCollection);
      const expiring = [];

      // Check each physician user for expiring credentials
      for (const docSnap of userSnapshot.docs) {
        const userData = docSnap.data();
        if (userData.userType === 'Physician' && userData.credentials && typeof userData.credentials === 'object') {
          const userName = `${userData.firstName || ''} ${userData.lastName || ''}`.trim() || userData.email;
          // Use the helper function with immediateCheck=false for scheduled checks
          await checkAndNotifyExpiringCredentials(userData, userName, false);
        }
      }

      // Also collect expiring credentials for the table display (within 3 months)
      const threeMonthsFromNow = new Date();
      threeMonthsFromNow.setMonth(threeMonthsFromNow.getMonth() + 3);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      userSnapshot.forEach(doc => {
        const userData = doc.data();
        if (userData.userType === 'Physician' && userData.credentials && typeof userData.credentials === 'object') {
          const creds = userData.credentials;
          const userName = `${userData.firstName || ''} ${userData.lastName || ''}`.trim() || userData.email;
          
          const checkCredential = (credentialKey, credentialName, expirationDate) => {
            if (!expirationDate) return;
            const expDate = new Date(expirationDate);
            expDate.setHours(0, 0, 0, 0);
            const daysUntilExpiration = Math.ceil((expDate - today) / (1000 * 60 * 60 * 24));
            if (expDate <= threeMonthsFromNow && expDate >= today) {
              expiring.push({
                userId: doc.id,
                userName,
                practice: userData.practice || 'N/A',
                credentialName,
                expirationDate,
                daysUntilExpiration
              });
            }
          };
          
          checkCredential('medicalLicense', 'Medical License', creds.medicalLicense?.expirationDate);
          checkCredential('dentalLicense', 'Dental License', creds.dentalLicense?.expirationDate);
          checkCredential('deaCertification', 'DEA Certification', creds.deaCertification?.expirationDate);
          checkCredential('malpracticeInsurance', 'Malpractice Insurance', creds.malpracticeInsurance?.expirationDate);
        }
      });

      // Sort by days until expiration (soonest first)
      expiring.sort((a, b) => a.daysUntilExpiration - b.daysUntilExpiration);
      setExpiringCredentials(expiring);

      if (expiring.length > 0) {
        toast.success(`Found ${expiring.length} credential(s) expiring within 3 months`);
      } else {
        toast.info('No credentials expiring within 3 months');
      }
    } catch (error) {
      console.error('Error checking expiring credentials:', error);
      toast.error('Failed to check expiring credentials');
    } finally {
      setIsCheckingCredentials(false);
    }
  }, []);

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData({ ...passwordData, [name]: value });
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-gray-50 via-white to-gray-50">
        <div className="text-center">
          <div className="inline-block size-8 animate-spin rounded-full border-4 border-solid border-indigo-600 border-r-transparent"></div>
          <p className="mt-4 text-sm text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 [&_*:focus]:outline-none [&_*:focus-visible]:outline [&_*:focus-visible]:outline-2 [&_*:focus-visible]:outline-offset-2 [&_*:focus-visible]:outline-indigo-600">
      <div className="mx-auto max-w-7xl lg:flex lg:gap-x-16 lg:px-8">
        {/* Header */}
        <div className="border-b border-gray-900/5 px-4 py-6 sm:px-6 lg:hidden">
          <h1 className="text-2xl font-bold tracking-tight text-gray-900">Settings</h1>
          <p className="mt-1 text-sm text-gray-500">Manage your account and system preferences</p>
      </div>

        {/* Sidebar Navigation */}
        <aside className="flex overflow-x-auto border-b border-gray-900/5 py-4 lg:block lg:w-64 lg:flex-none lg:border-0 lg:py-20">
          <nav className="flex-none px-4 sm:px-6 lg:px-0">
            <div className="hidden lg:block mb-8">
              <h1 className="text-2xl font-bold tracking-tight text-gray-900">Settings</h1>
              <p className="mt-1 text-sm text-gray-500">Manage your account and system preferences</p>
            </div>
            <ul role="list" className="flex gap-x-3 gap-y-1 whitespace-nowrap lg:flex-col">
              {availableTabs.map((tab) => {
                const IconComponent = tab.icon;
                return (
                  <li key={tab.id}>
          <button
            onClick={() => setActiveTab(tab.id)}
                      className={`group flex w-full gap-x-3 rounded-md py-2 pl-2 pr-3 text-sm font-semibold transition-colors outline-none ${
                        activeTab === tab.id
                          ? 'bg-gray-50 text-indigo-600'
                          : 'text-gray-700 hover:bg-gray-50 hover:text-indigo-600'
                      }`}
                    >
                      <IconComponent
                        aria-hidden="true"
                        className={`size-6 shrink-0 ${
                          activeTab === tab.id ? 'text-indigo-600' : 'text-gray-400 group-hover:text-indigo-600'
                        }`}
                      />
                      {tab.label}
          </button>
                  </li>
                );
              })}
            </ul>
          </nav>
        </aside>

        {/* Main Content */}
        <main className="px-4 py-8 sm:px-6 lg:flex-auto lg:px-0 lg:py-20">
          <div className={`mx-auto space-y-16 sm:space-y-20 lg:mx-0 lg:max-w-none`}>
            {/* Profile Tab */}
        {activeTab === 'profile' && (
              <>
                {/* Personal Information */}
                <div>
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="text-base font-semibold text-gray-900">Personal Information</h2>
                      <p className="mt-1 text-sm text-gray-500">Your account details and contact information.</p>
                  </div>
                    <button
                      type="button"
                      onClick={() => {
                        // Ensure profileData is properly initialized before opening edit modal
                        const currentUserType = profileData.userType || userInfo?.userType || 'Office Staff';
                        let credentials = profileData.credentials || userInfo?.credentials || {};
                        
                        if (currentUserType === 'Physician' && (!credentials || !credentials.medicalLicense)) {
                          credentials = initializePhysicianCredentials();
                        }
                        
                        // Store original credentials BEFORE opening modal (for comparison later)
                        if (currentUserType === 'Physician' && credentials) {
                          setOriginalCredentials(JSON.parse(JSON.stringify(credentials)));
                        } else {
                          setOriginalCredentials(null);
                        }
                        
                        // Set profileData with current values before opening modal
                        const currentPractices = profileData.practices || userInfo?.practices || 
                          (profileData.practice || userInfo?.practice ? [profileData.practice || userInfo?.practice] : []);
                        setProfileData({
                          firstName: profileData.firstName || userInfo?.firstName || '',
                          lastName: profileData.lastName || userInfo?.lastName || '',
                          email: profileData.email || userInfo?.email || '',
                          mobile: profileData.mobile || formatPhoneNumber(userInfo?.mobile || ''),
                          practice: profileData.practice || userInfo?.practice || '',
                          practices: currentPractices, // Include practices array
                          role: profileData.role || userInfo?.role || '',
                          userType: currentUserType,
                          credentials: credentials
                        });
                        setIsEditingProfile(true);
                      }}
                      className="rounded-md bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 transition-colors"
                    >
                      Edit
                    </button>
                  </div>

                  <dl className="mt-6 divide-y divide-gray-100 border-t border-gray-200 text-sm">
                    <div className="py-6 sm:flex">
                      <dt className="font-medium text-gray-900 sm:w-64 sm:flex-none sm:pr-6">Full name</dt>
                      <dd className="mt-1 text-gray-900 sm:mt-0 sm:flex-auto">
                        {profileData.firstName} {profileData.lastName}
                      </dd>
                </div>
                    <div className="py-6 sm:flex">
                      <dt className="font-medium text-gray-900 sm:w-64 sm:flex-none sm:pr-6">Email address</dt>
                      <dd className="mt-1 text-gray-900 sm:mt-0 sm:flex-auto">{profileData.email}</dd>
                  </div>
                    <div className="py-6 sm:flex">
                      <dt className="font-medium text-gray-900 sm:w-64 sm:flex-none sm:pr-6">Mobile</dt>
                      <dd className="mt-1 text-gray-900 sm:mt-0 sm:flex-auto">{profileData.mobile || '—'}</dd>
                  </div>
                    <div className="py-6 sm:flex">
                      <dt className="font-medium text-gray-900 sm:w-64 sm:flex-none sm:pr-6">
                        Practice{(profileData.practices?.length > 1) ? 's' : ''}
                      </dt>
                      <dd className="mt-1 text-gray-900 sm:mt-0 sm:flex-auto">
                        {profileData.practices && profileData.practices.length > 0 ? (
                          profileData.practices.length === 1 ? (
                            profileData.practices[0]
                          ) : (
                            <ul className="list-disc list-inside space-y-1">
                              {profileData.practices.map((practice, index) => (
                                <li key={index}>{practice}</li>
                              ))}
                            </ul>
                          )
                        ) : (
                          profileData.practice || '—'
                        )}
                      </dd>
                </div>
                    <div className="py-6 sm:flex">
                      <dt className="font-medium text-gray-900 sm:w-64 sm:flex-none sm:pr-6">Role</dt>
                      <dd className="mt-1 text-gray-900 sm:mt-0 sm:flex-auto">{profileData.role || '—'}</dd>
                  </div>
                    <div className="py-6 sm:flex">
                      <dt className="font-medium text-gray-900 sm:w-64 sm:flex-none sm:pr-6">User Type</dt>
                      <dd className="mt-1 text-gray-900 sm:mt-0 sm:flex-auto">{profileData.userType || 'Office Staff'}</dd>
                  </div>
                  </dl>

                  {/* Credentials Display Section (only for Physicians) */}
                  {(profileData.userType === 'Physician' || userInfo?.userType === 'Physician') && (
                    <div className="mt-8 border-t border-gray-200 pt-6">
                      <h3 className="text-base font-semibold text-gray-900 mb-4">Physician Credentials</h3>
                      <dl className="divide-y divide-gray-100 text-sm">
                        {/* Medical License */}
                        {(profileData.credentials?.medicalLicense?.number || profileData.credentials?.medicalLicense?.expirationDate) && (
                          <div className="py-4">
                            <dt className="font-medium text-gray-900 mb-2">Medical License</dt>
                            <dd className="mt-1 text-gray-600 space-y-1">
                              {profileData.credentials.medicalLicense.number && (
                                <div>Number: {profileData.credentials.medicalLicense.number}</div>
                              )}
                              {profileData.credentials.medicalLicense.expirationDate && (
                                <div>Expiration: {formatDateString(profileData.credentials.medicalLicense.expirationDate)}</div>
                              )}
                            </dd>
                          </div>
                        )}

                        {/* Dental License */}
                        {(profileData.credentials?.dentalLicense?.number || profileData.credentials?.dentalLicense?.expirationDate) && (
                          <div className="py-4">
                            <dt className="font-medium text-gray-900 mb-2">Dental License</dt>
                            <dd className="mt-1 text-gray-600 space-y-1">
                              {profileData.credentials.dentalLicense.number && (
                                <div>Number: {profileData.credentials.dentalLicense.number}</div>
                              )}
                              {profileData.credentials.dentalLicense.expirationDate && (
                                <div>Expiration: {formatDateString(profileData.credentials.dentalLicense.expirationDate)}</div>
                              )}
                            </dd>
                          </div>
                        )}

                        {/* DEA Certification */}
                        {(profileData.credentials?.deaCertification?.number || profileData.credentials?.deaCertification?.expirationDate) && (
                          <div className="py-4">
                            <dt className="font-medium text-gray-900 mb-2">DEA Certification</dt>
                            <dd className="mt-1 text-gray-600 space-y-1">
                              {profileData.credentials.deaCertification.number && (
                                <div>Number: {profileData.credentials.deaCertification.number}</div>
                              )}
                              {profileData.credentials.deaCertification.expirationDate && (
                                <div>Expiration: {formatDateString(profileData.credentials.deaCertification.expirationDate)}</div>
                              )}
                            </dd>
                          </div>
                        )}

                        {/* CME */}
                        {(profileData.credentials?.cme?.creditHours || (profileData.credentials?.cme?.certificates && profileData.credentials.cme.certificates.length > 0)) && (
                          <div className="py-4">
                            <dt className="font-medium text-gray-900 mb-2">Continuing Medical Education (CME)</dt>
                            <dd className="mt-1 text-gray-600 space-y-1">
                              {profileData.credentials.cme.creditHours && (
                                <div>Credit Hours: {profileData.credentials.cme.creditHours}</div>
                              )}
                              {profileData.credentials.cme.certificates && profileData.credentials.cme.certificates.length > 0 && (
                                <div className="mt-2">
                                  <div className="text-xs font-medium text-gray-700 mb-1">Certificates:</div>
                                  <div className="space-y-1">
                                    {profileData.credentials.cme.certificates.map((cert, index) => (
                                      <a
                                        key={index}
                                        href={cert.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-indigo-600 hover:text-indigo-500 text-xs block"
                                      >
                                        {cert.fileName}
                                      </a>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </dd>
                          </div>
                        )}

                        {/* Malpractice Insurance */}
                        {profileData.credentials?.malpracticeInsurance?.expirationDate && (
                          <div className="py-4">
                            <dt className="font-medium text-gray-900 mb-2">Malpractice Insurance</dt>
                            <dd className="mt-1 text-gray-600">
                              Expiration: {formatDateString(profileData.credentials.malpracticeInsurance.expirationDate)}
                            </dd>
                          </div>
                        )}

                        {/* Show message if no credentials are filled */}
                        {(!profileData.credentials?.medicalLicense?.number && 
                          !profileData.credentials?.medicalLicense?.expirationDate &&
                          !profileData.credentials?.dentalLicense?.number &&
                          !profileData.credentials?.dentalLicense?.expirationDate &&
                          !profileData.credentials?.deaCertification?.number &&
                          !profileData.credentials?.deaCertification?.expirationDate &&
                          !profileData.credentials?.cme?.creditHours &&
                          (!profileData.credentials?.cme?.certificates || profileData.credentials.cme.certificates.length === 0) &&
                          !profileData.credentials?.malpracticeInsurance?.expirationDate) && (
                          <div className="py-4">
                            <dd className="text-gray-500 italic">No credentials have been added yet. Click "Edit" to add your credentials.</dd>
                          </div>
                        )}
                      </dl>
                    </div>
                  )}
                  </div>

                {/* Change Password */}
                <div>
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="text-base font-semibold text-gray-900">Change Password</h2>
                      <p className="mt-1 text-sm text-gray-500">Update your password associated with your account.</p>
                </div>
                    <button
                      type="button"
                      onClick={() => setIsChangingPassword(true)}
                      className="rounded-md bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 transition-colors"
                    >
                      Change
                    </button>
            </div>

                  <div className="mt-6 rounded-lg bg-gray-50 px-4 py-6 text-sm text-gray-500">
                    Password changes are not visible for security reasons. Click "Change" to update your password.
                </div>
                </div>
              </>
        )}

            {/* User Management Tab */}
        {activeTab === 'users' && (
              <div>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div>
                    <h2 className="text-base font-semibold text-gray-900">User Management</h2>
                    <p className="mt-1 text-sm text-gray-500">Manage user accounts and permissions.</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={downloadUserCSVTemplate}
                      className="inline-flex items-center gap-x-2 rounded-md bg-white px-4 py-2.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 transition-colors"
                    >
                      <FaDownload className="-ml-0.5 size-4" />
                      Download Template
                    </button>
                    <label className="inline-flex items-center gap-x-2 rounded-md bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 transition-colors cursor-pointer">
                      <FaUpload className="-ml-0.5 size-4" />
                      {isBulkUploadingUsers ? 'Uploading...' : 'Bulk Upload'}
                      <input
                        type="file"
                        accept=".csv"
                        onChange={handleUserCSVUpload}
                        disabled={isBulkUploadingUsers}
                        className="hidden"
                      />
                    </label>
                    <button
                      onClick={exportUsersCSV}
                      disabled={isExportingUsers || users.length === 0}
                      className="inline-flex items-center gap-x-2 rounded-md bg-white px-4 py-2.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <FaDownload className="-ml-0.5 size-4" />
                      {isExportingUsers ? 'Exporting...' : 'Export'}
                    </button>
                    <button
                      onClick={handleAddNewUser}
                      className="inline-flex items-center justify-center gap-x-2 rounded-md bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 transition-colors"
                    >
                      <FaPlus className="-ml-0.5 size-4" />
                      Add User
                    </button>
                  </div>
              </div>

              {/* Bulk User Upload Progress */}
              {isBulkUploadingUsers && (
                <div className="mt-4 rounded-lg bg-indigo-50 px-4 py-3 ring-1 ring-inset ring-indigo-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="inline-block size-4 animate-spin rounded-full border-2 border-solid border-indigo-600 border-r-transparent"></div>
                      <span className="text-sm font-medium text-indigo-900">Uploading users...</span>
                    </div>
                    <div className="text-sm text-indigo-700">
                      Success: {bulkUserUploadProgress.success} | Skipped: {bulkUserUploadProgress.skipped} | Errors: {bulkUserUploadProgress.errors}
                    </div>
                  </div>
                </div>
              )}

              {userInfo.role === 'System Administrator' && (
                  <div className="mt-6">
                    <label className="block text-sm font-medium text-gray-900">
                    Filter by Practice:
                    </label>
                    <div className="mt-2">
                    <select
                      value={selectedPractice}
                      onChange={(e) => setSelectedPractice(e.target.value)}
                        className="block w-full rounded-md bg-white px-3 py-2 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-indigo-600 sm:text-sm"
                    >
                      <option value="">All Practices</option>
                      {[...new Set(practices.map(p => (p.practiceName || '').trim()).filter(Boolean))].sort().map((name) => (
                        <option key={name} value={name}>{name}</option>
                      ))}
                    </select>
                    </div>
                </div>
              )}

                {selectedUserIds.size > 0 && !isBulkDeletingUsers && (
                  <div className="mt-4 flex items-center gap-3 rounded-lg bg-red-50 px-4 py-3 ring-1 ring-inset ring-red-200">
                    <span className="text-sm font-medium text-red-800">{selectedUserIds.size} user{selectedUserIds.size !== 1 ? 's' : ''} selected</span>
                    <button
                      onClick={handleBulkDeleteUsers}
                      className="inline-flex items-center gap-1.5 rounded-md bg-red-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-red-500"
                    >
                      <FaTrash className="size-3" /> Delete Selected
                    </button>
                    <button
                      onClick={() => setSelectedUserIds(new Set())}
                      className="text-xs text-red-600 hover:text-red-800 font-medium"
                    >
                      Clear Selection
                    </button>
                  </div>
                )}
                {isBulkDeletingUsers && (
                  <div className="mt-4 rounded-lg bg-red-50 px-4 py-3 ring-1 ring-inset ring-red-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="inline-block size-4 animate-spin rounded-full border-2 border-solid border-red-600 border-r-transparent"></div>
                        <span className="text-sm font-medium text-red-900">Deleting users...</span>
                      </div>
                      <span className="text-sm text-red-700">
                        {bulkDeleteUserProgress.processed} of {bulkDeleteUserProgress.total} processed · {bulkDeleteUserProgress.deleted} deleted
                      </span>
                    </div>
                  </div>
                )}

                <div className="mt-8 w-full">
                  <div className="sm:rounded-lg shadow ring-1 ring-black ring-opacity-5 overflow-hidden w-full">
                    <div className="overflow-x-auto -mx-4 sm:mx-0">
                      <div className="inline-block min-w-full align-middle">
                        <table className="min-w-full divide-y divide-gray-300">
                          <thead className="bg-gray-50">
                            <tr>
                              <th scope="col" className="w-10 py-3.5 pl-4 sm:pl-6">
                                <input
                                  type="checkbox"
                                  checked={users.length > 0 && selectedUserIds.size === users.filter(u => canDeleteUser(u)).length && users.filter(u => canDeleteUser(u)).length > 0}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setSelectedUserIds(new Set(users.filter(u => canDeleteUser(u)).map(u => u.id)));
                                    } else {
                                      setSelectedUserIds(new Set());
                                    }
                                  }}
                                  className="size-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                                />
                              </th>
                              <th scope="col" className="py-3.5 pl-2 pr-3 text-left text-sm font-semibold text-gray-900">
                                Name
                              </th>
                              <th scope="col" className="hidden md:table-cell px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                Role
                              </th>
                              <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                Status
                              </th>
                              <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6 text-right text-sm font-semibold text-gray-900">
                                Actions
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200 bg-white">
                            {users.map((user, index) => (
                              <tr key={user.id} className={`${index % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'} ${selectedUserIds.has(user.id) ? 'bg-indigo-50' : ''} hover:bg-indigo-50/30 transition-colors`}>
                                <td className="w-10 py-4 pl-4 sm:pl-6">
                                  {canDeleteUser(user) && (
                                    <input
                                      type="checkbox"
                                      checked={selectedUserIds.has(user.id)}
                                      onChange={(e) => {
                                        const next = new Set(selectedUserIds);
                                        if (e.target.checked) next.add(user.id);
                                        else next.delete(user.id);
                                        setSelectedUserIds(next);
                                      }}
                                      className="size-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                                    />
                                  )}
                                </td>
                                <td className="py-4 pl-2 pr-3 text-sm font-medium text-gray-900">
                                  <div className="flex flex-col">
                                    <span className="font-medium">{user.firstName} {user.lastName}</span>
                                    {user.email && (
                                      <span className="text-xs text-gray-500 mt-0.5">{user.email}</span>
                                    )}
                                    {user.mobile && formatPhoneNumber(user.mobile) && (
                                      <span className="text-xs text-gray-500 mt-0.5">{formatPhoneNumber(user.mobile)}</span>
                                    )}
                                  </div>
                                </td>
                                <td className="hidden md:table-cell px-3 py-4 text-sm text-gray-500">
                                  <span className="inline-flex items-center rounded-md bg-indigo-50 px-2 py-1 text-xs font-medium text-indigo-700 ring-1 ring-inset ring-indigo-700/10">
                                    {user.role}
                                  </span>
                                </td>
                                <td className="px-3 py-4 text-sm text-gray-500">
                                  <span className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset ${
                                    user.status === 'Active'
                                      ? 'bg-green-50 text-green-700 ring-green-600/20'
                                      : user.status === 'Pending'
                                      ? 'bg-yellow-50 text-yellow-700 ring-yellow-600/20'
                                      : 'bg-red-50 text-red-700 ring-red-600/20'
                                  }`}>
                                    {user.status}
                                  </span>
                                </td>
                                <td className="whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                                  <div className="flex items-center justify-end gap-2">
                                    <button 
                                      onClick={() => handleEditUser(user.id)}
                                      className="text-indigo-600 hover:text-indigo-900 bg-indigo-50 p-1.5 rounded-md"
                                      title="Edit"
                                    >
                                      <FaEdit className="size-4" />
                                    </button>
                                    <button 
                                      onClick={() => handleDisableUser(user.id)}
                                      className={`${user.status === 'Active' ? 'text-green-600 hover:text-green-900 bg-green-50' : 'text-red-600 hover:text-red-900 bg-red-50'} p-1.5 rounded-md`}
                                      title={user.status === 'Active' ? 'Deactivate' : 'Activate'}
                                    >
                                      {user.status === 'Active' ? <FaToggleOn className="size-4" /> : <FaToggleOff className="size-4" />}
                                    </button>
                                    {canDeleteUser(user) && (
                                      <button 
                                        onClick={() => handleDeleteUser(user.id, `${user.firstName} ${user.lastName}`)}
                                        className="text-red-600 hover:text-red-900 bg-red-100 p-1.5 rounded-md"
                                        title="Delete User"
                                      >
                                        <FaTrash className="size-4" />
                                      </button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
        )}

            {/* Practice Management Tab */}
        {(activeTab === 'practice' || activeTab === 'practices') && (
              <div>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div>
                    <h2 className="text-base font-semibold text-gray-900">Practice Management</h2>
                    <p className="mt-1 text-sm text-gray-500">Manage practice information and settings.</p>
                  </div>
                <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                  {userInfo.role === 'System Administrator' && (
                    <>
                      <button
                        onClick={downloadCSVTemplate}
                        className="inline-flex items-center gap-x-2 rounded-md bg-white px-4 py-2.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 transition-colors"
                      >
                        <FaDownload className="-ml-0.5 size-4" />
                        Download Template
                      </button>
                      <label className="inline-flex items-center gap-x-2 rounded-md bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 transition-colors cursor-pointer">
                        <FaUpload className="-ml-0.5 size-4" />
                        {isBulkUploading ? 'Uploading...' : 'Bulk Upload'}
                        <input
                          type="file"
                          accept=".csv"
                          onChange={handleCSVUpload}
                          disabled={isBulkUploading}
                          className="hidden"
                        />
                      </label>
                    </>
                  )}
                  <button
                    onClick={exportPracticesCSV}
                    disabled={practices.length === 0}
                    className="inline-flex items-center gap-x-2 rounded-md bg-white px-4 py-2.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <FaDownload className="-ml-0.5 size-4" />
                    Export
                  </button>
                  {(userInfo.role === 'System Administrator' || (userInfo.role === 'Practice Administrator' && practices.length === 0)) && (
                    <button
                      onClick={handleAddPractice}
                      className="inline-flex items-center gap-x-2 rounded-md bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 transition-colors"
                    >
                      <FaPlus className="-ml-0.5 size-4" />
                      Add Practice
                    </button>
                  )}
                </div>
              </div>

              {/* Bulk Upload Progress */}
              {isBulkUploading && (
                <div className="mt-4 rounded-lg bg-indigo-50 px-4 py-3 ring-1 ring-inset ring-indigo-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="inline-block size-4 animate-spin rounded-full border-2 border-solid border-indigo-600 border-r-transparent"></div>
                      <span className="text-sm font-medium text-indigo-900">Uploading practices...</span>
                    </div>
                    <div className="text-sm text-indigo-700">
                      Success: {bulkUploadProgress.success} | Skipped: {bulkUploadProgress.skipped} | Errors: {bulkUploadProgress.errors}
                    </div>
                  </div>
                </div>
              )}

              {userInfo.role === 'System Administrator' && (
                <div className="mt-6">
                  <label className="block text-sm font-medium text-gray-900">
                    Filter by Practice:
                  </label>
                  <div className="mt-2">
                    <select
                      value={selectedPractice}
                      onChange={(e) => setSelectedPractice(e.target.value)}
                      className="block w-full rounded-md bg-white px-3 py-2 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-indigo-600 sm:text-sm"
                    >
                      <option value="">All Practices</option>
                      {[...new Set(practices.map(p => (p.practiceName || '').trim()).filter(Boolean))].sort().map((name) => (
                        <option key={name} value={name}>{name}</option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

                {selectedPracticeIds.size > 0 && userInfo.role === 'System Administrator' && !isBulkDeletingPractices && (
                  <div className="mt-4 flex items-center gap-3 rounded-lg bg-red-50 px-4 py-3 ring-1 ring-inset ring-red-200">
                    <span className="text-sm font-medium text-red-800">{selectedPracticeIds.size} practice{selectedPracticeIds.size !== 1 ? 's' : ''} selected</span>
                    <button
                      onClick={handleBulkDeletePractices}
                      className="inline-flex items-center gap-1.5 rounded-md bg-red-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-red-500"
                    >
                      <FaTrash className="size-3" /> Delete Selected
                    </button>
                    <button
                      onClick={() => setSelectedPracticeIds(new Set())}
                      className="text-xs text-red-600 hover:text-red-800 font-medium"
                    >
                      Clear Selection
                    </button>
                  </div>
                )}
                {isBulkDeletingPractices && (
                  <div className="mt-4 rounded-lg bg-red-50 px-4 py-3 ring-1 ring-inset ring-red-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="inline-block size-4 animate-spin rounded-full border-2 border-solid border-red-600 border-r-transparent"></div>
                        <span className="text-sm font-medium text-red-900">Deleting practices...</span>
                      </div>
                      <span className="text-sm text-red-700">
                        {bulkDeletePracticeProgress.processed} of {bulkDeletePracticeProgress.total} processed · {bulkDeletePracticeProgress.deleted} deleted{bulkDeletePracticeProgress.skipped > 0 ? ` · ${bulkDeletePracticeProgress.skipped} skipped` : ''}
                      </span>
                    </div>
                  </div>
                )}

                <div className="mt-8 w-full">
                  <div className="overflow-hidden sm:rounded-lg shadow ring-1 ring-black ring-opacity-5 w-full">
                    <div className="overflow-x-auto -mx-4 sm:mx-0">
                      <div className="inline-block min-w-full align-middle">
                        <table className="min-w-full divide-y divide-gray-300">
                          <thead className="bg-gray-50">
                            <tr>
                              {userInfo.role === 'System Administrator' && (
                                <th scope="col" className="w-10 py-3.5 pl-4 sm:pl-6">
                                  <input
                                    type="checkbox"
                                    checked={practices.length > 0 && selectedPracticeIds.size === practices.length}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        setSelectedPracticeIds(new Set(practices.map(p => p.id)));
                                      } else {
                                        setSelectedPracticeIds(new Set());
                                      }
                                    }}
                                    className="size-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                                  />
                                </th>
                              )}
                              <th scope="col" className="py-3.5 pl-2 pr-3 text-left text-sm font-semibold text-gray-900">
                                Name
                              </th>
                              <th scope="col" className="hidden 2xl:table-cell px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                Office / City
                              </th>
                              <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                Status
                              </th>
                              <th scope="col" className="py-3.5 pl-3 pr-4 text-right text-sm font-semibold text-gray-900 sm:pr-6">
                                Actions
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200 bg-white">
                            {(() => {
                              const byName = {};
                              for (const p of practices) {
                                const name = (p.practiceName || '').trim();
                                if (!name) continue;
                                if (!byName[name]) {
                                  byName[name] = { ...p, _locs: getPracticeLocations(p) };
                                } else {
                                  const existing = byName[name]._locs.map(l => l.name);
                                  for (const loc of getPracticeLocations(p)) {
                                    if (!existing.includes(loc.name)) byName[name]._locs.push(loc);
                                  }
                                }
                              }
                              const grouped = Object.values(byName).filter(p =>
                                !selectedPractice || practiceMatchesTarget(p, selectedPractice)
                              );
                              return grouped.map((practice, index) => {
                                const locs = practice._locs || [];
                                const hasLocations = locs.some(l => l.name);
                                return (
                                <tr key={practice.id} className={`${index % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'} ${selectedPracticeIds.has(practice.id) ? 'bg-red-50' : ''} hover:bg-indigo-50/30 transition-colors`}>
                                  {userInfo.role === 'System Administrator' && (
                                    <td className="w-10 py-4 pl-4 sm:pl-6">
                                      <input
                                        type="checkbox"
                                        checked={selectedPracticeIds.has(practice.id)}
                                        onChange={(e) => {
                                          const next = new Set(selectedPracticeIds);
                                          if (e.target.checked) next.add(practice.id);
                                          else next.delete(practice.id);
                                          setSelectedPracticeIds(next);
                                        }}
                                        className="size-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                                      />
                                    </td>
                                  )}
                                  <td className="py-4 pl-2 pr-3 text-sm text-gray-900">
                                    <div className="flex flex-col">
                                      <span className="font-semibold">{practice.practiceName}</span>
                                      {hasLocations ? (
                                        <div className="mt-1.5 space-y-1">
                                          {locs.filter(l => l.name).map((loc, li) => (
                                            <div key={li} className="flex flex-col pl-3 border-l-2 border-indigo-200">
                                              <span className="text-xs font-medium text-indigo-600">{loc.name}</span>
                                              {(loc.adminLead || loc.adminCell || loc.adminEmail) && (
                                                <span className="text-xs text-gray-500">
                                                  {[loc.adminLead, loc.adminCell ? formatPhoneNumber(loc.adminCell) : '', loc.adminEmail].filter(Boolean).join(' · ')}
                                                </span>
                                              )}
                                            </div>
                                          ))}
                                        </div>
                                      ) : (
                                        <>
                                          {(practice.adminLead || practice.email || practice.officePhone) && (
                                            <span className="text-xs text-gray-500 mt-0.5">
                                              {[practice.adminLead, practice.officePhone ? formatPhoneNumber(practice.officePhone) : '', practice.email].filter(Boolean).join(' · ')}
                                            </span>
                                          )}
                                        </>
                                      )}
                                    </div>
                                  </td>
                                  <td className="hidden 2xl:table-cell px-3 py-4 text-sm text-gray-500 align-top">
                                    {hasLocations ? locs.filter(l => l.name).map(l => l.name).join(', ') : (practice.officeCity && practice.officeState ? `${practice.officeCity}, ${practice.officeState}` : null) || '—'}
                                  </td>
                                  <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                    <span className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset ${
                                      practice.status === 'Active'
                                        ? 'bg-green-50 text-green-700 ring-green-600/20'
                                        : 'bg-red-50 text-red-700 ring-red-600/20'
                                    }`}>
                                      {practice.status}
                                    </span>
                                  </td>
                                  <td className="whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                                    <div className="flex items-center justify-end gap-1.5">
                                      <button 
                                        onClick={() => handleEditPractice(practice.id)}
                                        className="rounded-md bg-indigo-50 p-1.5 text-indigo-600 shadow-sm ring-1 ring-inset ring-indigo-600/20 hover:bg-indigo-100 outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 transition-colors"
                                        title="Edit"
                                      >
                                        <FaEdit className="size-3.5" />
                                      </button>
                                      {userInfo.role === 'System Administrator' && (
                                        <>
                                          <button 
                                            onClick={() => handleDisablePractice(practice.id)}
                                            className={`rounded-md p-1.5 shadow-sm ring-1 ring-inset outline-none transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 ${
                                              practice.status === 'Active'
                                                ? 'bg-green-50 text-green-600 ring-green-600/20 hover:bg-green-100 focus-visible:outline-green-600'
                                                : 'bg-red-50 text-red-600 ring-red-600/20 hover:bg-red-100 focus-visible:outline-red-600'
                                            }`}
                                            title={practice.status === 'Active' ? 'Deactivate' : 'Activate'}
                                          >
                                            {practice.status === 'Active' ? <FaToggleOn className="size-3.5" /> : <FaToggleOff className="size-3.5" />}
                                          </button>
                                          <button 
                                            onClick={() => handleDeletePractice(practice.id, practice.practiceName)}
                                            className="rounded-md bg-red-600 p-1.5 text-white shadow-sm hover:bg-red-500 outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600 transition-colors"
                                            title="Delete Practice"
                                          >
                                            <FaTrash className="size-3.5" />
                                          </button>
                                        </>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              );});
                            })()}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Physician Roster Section */}
                <div className="mt-12 border-t border-gray-200 pt-8">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                      <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2">
                        <FaUserMd className="size-5 text-indigo-600" />
                        Physician Roster
                      </h2>
                      <p className="mt-1 text-sm text-gray-500">
                        Manage physicians associated with {userInfo.role === 'Practice Administrator' ? 'your practice' : 'this practice'}. 
                        These physicians can be selected when filling out incident forms.
                      </p>
                    </div>
                    <button
                      onClick={handleAddPhysician}
                      className="inline-flex items-center gap-x-2 rounded-md bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 transition-colors"
                    >
                      <FaPlus className="-ml-0.5 size-4" />
                      Add Physician
                    </button>
                  </div>

                  {physicians.length === 0 ? (
                    <div className="mt-6 rounded-lg bg-gray-50 px-4 py-8 text-center">
                      <FaUserMd className="mx-auto size-12 text-gray-400" />
                      <h3 className="mt-2 text-sm font-medium text-gray-900">No physicians</h3>
                      <p className="mt-1 text-sm text-gray-500">
                        Get started by adding a physician to your practice roster.
                      </p>
                      <div className="mt-6">
                        <button
                          onClick={handleAddPhysician}
                          className="inline-flex items-center gap-x-2 rounded-md bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                        >
                          <FaPlus className="-ml-0.5 size-4" />
                          Add Physician
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="mt-6 w-full">
                      <div className="overflow-hidden sm:rounded-lg shadow ring-1 ring-black ring-opacity-5 w-full">
                        <div className="overflow-x-auto -mx-4 sm:mx-0">
                          <div className="inline-block min-w-full align-middle">
                            <table className="min-w-full divide-y divide-gray-300">
                              <thead className="bg-gray-50">
                                <tr>
                                  <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                                    Name
                                  </th>
                                  <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                    Specialty
                                  </th>
                                  <th scope="col" className="hidden sm:table-cell px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                    Practices
                                  </th>
                                  <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                    Status
                                  </th>
                                  <th scope="col" className="py-3.5 pl-3 pr-4 text-right text-sm font-semibold text-gray-900 sm:pr-6">
                                    Actions
                                  </th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-200 bg-white">
                                {physicians.map((physician, index) => {
                                  const managedInUsers = isPhysicianManagedInUsers(physician);
                                  return (
                                  <tr key={physician.id} className={`${index % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'} hover:bg-indigo-50/30 transition-colors`}>
                                    <td className="py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                                      <div className="flex flex-col">
                                        <span className="font-medium">{physician.lastName}, {physician.firstName}</span>
                                        {physician.email && (
                                          <span className="text-xs text-gray-500 mt-0.5">{physician.email}</span>
                                        )}
                                        {managedInUsers && (
                                          <span className="mt-1 inline-flex w-fit items-center rounded-md bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-700 ring-1 ring-inset ring-gray-300">
                                            Managed in User Management
                                          </span>
                                        )}
                                      </div>
                                    </td>
                                    <td className="px-3 py-4 text-sm text-gray-500">
                                      {physician.specialty || '—'}
                                    </td>
                                    <td className="hidden sm:table-cell px-3 py-4 text-sm text-gray-500">
                                      {physician.practiceCount > 1 ? (
                                        <span className="inline-flex items-center gap-1" title={physician.allPractices?.map(p => p.name).join(', ')}>
                                          <span className="inline-flex items-center rounded-md bg-indigo-50 px-2 py-1 text-xs font-medium text-indigo-700 ring-1 ring-inset ring-indigo-700/10">
                                            {physician.practiceCount} practices
                                          </span>
                                        </span>
                                      ) : (
                                        <span className="text-gray-500">1 practice</span>
                                      )}
                                    </td>
                                    <td className="px-3 py-4 text-sm text-gray-500">
                                      <span className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset ${
                                        physician.statusForPractice === 'Active'
                                          ? 'bg-green-50 text-green-700 ring-green-600/20'
                                          : 'bg-red-50 text-red-700 ring-red-600/20'
                                      }`}>
                                        {physician.statusForPractice}
                                      </span>
                                    </td>
                                    <td className="whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                                      <div className="flex items-center justify-end gap-1.5">
                                        {!managedInUsers && (
                                          <button 
                                            onClick={() => handleEditPhysician(physician.id)}
                                            className="rounded-md bg-indigo-50 p-1.5 text-indigo-600 shadow-sm ring-1 ring-inset ring-indigo-600/20 hover:bg-indigo-100 outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 transition-colors"
                                            title="Edit"
                                          >
                                            <FaEdit className="size-3.5" />
                                          </button>
                                        )}
                                        <button 
                                          onClick={() => handleTogglePhysicianStatus(physician.id)}
                                          className={`rounded-md p-1.5 shadow-sm ring-1 ring-inset outline-none transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 ${
                                            physician.statusForPractice === 'Active'
                                              ? 'bg-green-50 text-green-600 ring-green-600/20 hover:bg-green-100 focus-visible:outline-green-600'
                                              : 'bg-red-50 text-red-600 ring-red-600/20 hover:bg-red-100 focus-visible:outline-red-600'
                                          }`}
                                          title={physician.statusForPractice === 'Active' ? 'Deactivate for this practice' : 'Activate for this practice'}
                                        >
                                          {physician.statusForPractice === 'Active' ? <FaToggleOn className="size-3.5" /> : <FaToggleOff className="size-3.5" />}
                                        </button>
                                        {!managedInUsers && (
                                          <button
                                            onClick={() => handleDeletePhysician(physician.id)}
                                            className="rounded-md bg-red-50 p-1.5 text-red-600 shadow-sm ring-1 ring-inset ring-red-600/20 hover:bg-red-100 outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600 transition-colors"
                                            title={physician.practiceCount > 1 ? 'Remove from this practice' : 'Delete from roster'}
                                          >
                                            <FaTrash className="size-3.5" />
                                          </button>
                                        )}
                                      </div>
                                    </td>
                                  </tr>
                                );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
        )}

            {/* System Settings Tab */}
        {activeTab === 'system' && (
              <>
                <div>
                  <h2 className="text-base font-semibold text-gray-900">System Configuration</h2>
                  <p className="mt-1 text-sm text-gray-500">Overview of system statistics and configuration.</p>

                  <dl className="mt-6 divide-y divide-gray-100 border-t border-gray-200 text-sm">
                    <div className="py-6 sm:flex">
                      <dt className="font-medium text-gray-900 sm:w-64 sm:flex-none sm:pr-6">Total Users</dt>
                      <dd className="mt-1 text-gray-900 sm:mt-0 sm:flex-auto">{users.length}</dd>
                </div>
                    <div className="py-6 sm:flex">
                      <dt className="font-medium text-gray-900 sm:w-64 sm:flex-none sm:pr-6">Total Practices</dt>
                      <dd className="mt-1 text-gray-900 sm:mt-0 sm:flex-auto">{practices.length}</dd>
                </div>
                    <div className="py-6 sm:flex">
                      <dt className="font-medium text-gray-900 sm:w-64 sm:flex-none sm:pr-6">Active Users</dt>
                      <dd className="mt-1 text-gray-900 sm:mt-0 sm:flex-auto">
                    {users.filter(u => u.status === 'Active').length}
                      </dd>
                </div>
                    <div className="py-6 sm:flex">
                      <dt className="font-medium text-gray-900 sm:w-64 sm:flex-none sm:pr-6">System Version</dt>
                      <dd className="mt-1 text-gray-900 sm:mt-0 sm:flex-auto">2.0.0</dd>
                </div>
                  </dl>
            </div>

                <div>
                  <h2 className="text-base font-semibold text-gray-900">Email Routing</h2>
                  <p className="mt-1 text-sm text-gray-500">Configure email routing for form submissions.</p>
                  <div className="mt-6">
            <EmailRouting userInfo={userInfo} organization={organization} />
                  </div>
                </div>

                <div>
                  <h2 className="text-base font-semibold text-gray-900">Credential Expiration Management</h2>
                  <p className="mt-1 text-sm text-gray-500">View and manage physician credentials expiring within 3 months.</p>
                  <div className="mt-6">
                    <button
                      onClick={checkExpiringCredentials}
                      disabled={isCheckingCredentials}
                      className="inline-flex items-center gap-x-2 rounded-md bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isCheckingCredentials ? (
                        <>
                          <svg className="animate-spin -ml-0.5 size-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          <span>Checking...</span>
                        </>
                      ) : (
                        <>
                          <FaSyncAlt className="size-4" />
                          <span>Check Expiring Credentials</span>
                        </>
                      )}
                    </button>
                  </div>
                  {expiringCredentials.length > 0 && (
                    <div className="mt-6 overflow-hidden shadow outline outline-1 outline-black/5 sm:rounded-lg">
                      <table className="min-w-full divide-y divide-gray-300">
                        <thead className="bg-gray-50">
                          <tr>
                            <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                              Physician
                            </th>
                            <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                              Practice
                            </th>
                            <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                              Credential
                            </th>
                            <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                              Expiration Date
                            </th>
                            <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                              Days Until Expiration
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 bg-white">
                          {expiringCredentials.map((item, index) => (
                            <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}>
                              <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                                {item.userName}
                              </td>
                              <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                {item.practice}
                              </td>
                              <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                {item.credentialName}
                              </td>
                              <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                {formatDateString(item.expirationDate)}
                              </td>
                              <td className="whitespace-nowrap px-3 py-4 text-sm">
                                <span className={`inline-flex items-center rounded-md px-2.5 py-1 text-xs font-medium ring-1 ring-inset ${
                                  item.daysUntilExpiration <= 30
                                    ? 'bg-red-50 text-red-700 ring-red-600/20'
                                    : item.daysUntilExpiration <= 60
                                    ? 'bg-yellow-50 text-yellow-700 ring-yellow-600/20'
                                    : 'bg-orange-50 text-orange-700 ring-orange-600/20'
                                }`}>
                                  {item.daysUntilExpiration} days
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  {expiringCredentials.length === 0 && !isCheckingCredentials && (
                    <div className="mt-6 rounded-lg bg-gray-50 px-4 py-6 text-sm text-gray-500 text-center">
                      No credentials expiring within 3 months found.
                    </div>
                  )}
                </div>

                <div>
                  <h2 className="text-base font-semibold text-gray-900">Audit Log</h2>
                  <p className="mt-1 text-sm text-gray-500">System activity and audit trail.</p>
                  <div className="mt-6 rounded-lg bg-gray-50 px-4 py-6 text-sm text-gray-500">
                    Audit logging coming soon...
            </div>
          </div>
              </>
        )}
          </div>
        </main>
      </div>

      {/* User Edit/Add Modal */}
      <Dialog open={isEditingUser || isAddingUser} onClose={() => {
        setIsEditingUser(false);
        setIsAddingUser(false);
        setIsCreatingPhysicianUser(false);
      }} className="relative z-50">
        <DialogBackdrop
          transition
          className="fixed inset-0 bg-gray-500/75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
        />
        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <DialogPanel
              transition
              className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:w-full sm:max-w-2xl data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95"
            >
              <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                <div className="absolute right-0 top-0 hidden pr-4 pt-4 sm:block">
              <button 
                    type="button"
                onClick={() => {
                  setIsEditingUser(false);
                  setIsAddingUser(false);
                }}
                    className="rounded-md bg-white text-gray-400 hover:text-gray-500 outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
              >
                    <span className="sr-only">Close</span>
                    <FaTimes aria-hidden="true" className="size-6" />
              </button>
            </div>
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex size-12 shrink-0 items-center justify-center rounded-full bg-indigo-100 sm:mx-0 sm:size-10">
                    {isCreatingPhysicianUser ? (
                      <FaUserMd aria-hidden="true" className="size-6 text-indigo-600" />
                    ) : (
                      <FaUser aria-hidden="true" className="size-6 text-indigo-600" />
                    )}
                  </div>
                  <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                    <h3 className="text-base font-semibold text-gray-900 mb-2">
                      {isCreatingPhysicianUser 
                        ? 'Create Physician Account' 
                        : (isAddingUser ? 'Add New User' : 'Edit User')}
                    </h3>
                    {isCreatingPhysicianUser && (
                      <p className="text-sm text-gray-500">
                        Create a login account for a physician. They will be automatically added to the physician roster.
                      </p>
                    )}
                  </div>
                </div>

                <form onSubmit={handleSubmitUser} className="mt-6">
              <div className="space-y-6">
                <div className="grid grid-cols-1 gap-x-6 gap-y-6 sm:grid-cols-2">
                  <div>
                    <label htmlFor="firstName" className="block text-sm font-medium text-gray-900">
                      First Name <span className="text-red-500">*</span>
                    </label>
                    <div className="mt-2">
                  <input 
                    id="firstName"
                    name="firstName" 
                        type="text"
                    value={formData.firstName || ''} 
                    onChange={handleChange} 
                    required
                          className="block w-full rounded-md bg-white px-3 py-2 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-indigo-600 sm:text-sm"
                  />
                </div>
                  </div>

                  <div>
                    <label htmlFor="lastName" className="block text-sm font-medium text-gray-900">
                      Last Name <span className="text-red-500">*</span>
                    </label>
                    <div className="mt-2">
                  <input 
                    id="lastName"
                    name="lastName" 
                        type="text"
                    value={formData.lastName || ''} 
                    onChange={handleChange} 
                    required
                          className="block w-full rounded-md bg-white px-3 py-2 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-indigo-600 sm:text-sm"
                  />
                </div>
              </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-900">
                      Email <span className="text-red-500">*</span>
                    </label>
                    <div className="mt-2">
                  <input 
                    id="email"
                    name="email" 
                    type="email"
                    value={formData.email || ''} 
                    onChange={handleChange} 
                    required
                          className="block w-full rounded-md bg-white px-3 py-2 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-indigo-600 sm:text-sm"
                  />
                </div>
                  </div>

                  <div>
                    <label htmlFor="mobile" className="block text-sm font-medium text-gray-900">
                      Mobile
                    </label>
                    <div className="mt-2">
                  <input 
                    id="mobile"
                    name="mobile" 
                    type="tel"
                    value={formData.mobile || ''} 
                    onChange={handleChange}
                    placeholder="(XXX) XXX-XXXX"
                    maxLength="14"
                          className="block w-full rounded-md bg-white px-3 py-2 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-indigo-600 sm:text-sm"
                  />
                </div>
              </div>

                {formData.role !== 'System Administrator' && (
                    <div className="sm:col-span-2">
                      <label className="block text-sm font-medium text-gray-900">
                        Practice{userInfo.role === 'System Administrator' ? 's' : ''} <span className="text-red-500">*</span>
                      </label>
                      <p className="mt-1 text-xs text-gray-500">
                        {userInfo.role === 'System Administrator' 
                          ? 'Select one or more practices to assign this user to.'
                          : 'The practice this user belongs to.'}
                      </p>
                      <div className="mt-2">
                        {userInfo.role === 'System Administrator' ? (
                          // Multi-practice selection for System Administrators
                          <div className="max-h-48 overflow-y-auto rounded-md border border-gray-300 bg-white p-3 space-y-2">
                            {[...new Set(practices.filter(p => p.status === 'Active').map(p => (p.practiceName || '').trim()).filter(Boolean))].sort().map((name) => (
                              <label key={name} className="flex items-center gap-3 cursor-pointer hover:bg-gray-50 p-1.5 rounded">
                                <input
                                  type="checkbox"
                                  checked={(formData.practices || []).some(p => {
                                    const n = normalizePracticeName(name);
                                    const up = normalizePracticeName(String(p || ''));
                                    return up === n || up.startsWith(n + ' (');
                                  })}
                                  onChange={(e) => {
                                    const currentPractices = formData.practices || [];
                                    const practiceId = name;
                                    if (e.target.checked) {
                                      const hasMatch = currentPractices.some(p => normalizePracticeName(p) === normalizePracticeName(name) || normalizePracticeName(String(p)).startsWith(normalizePracticeName(name) + ' ('));
                                      if (hasMatch) return;
                                      setFormData(prev => ({
                                        ...prev,
                                        practices: [...currentPractices, name],
                                        practice: currentPractices.length === 0 ? name : prev.practice
                                      }));
                                    } else {
                                      const n = normalizePracticeName(name);
                                      const newPractices = currentPractices.filter(p => {
                                        const up = normalizePracticeName(String(p || ''));
                                        return up !== n && !up.startsWith(n + ' (');
                                      });
                                      setFormData(prev => ({
                                        ...prev,
                                        practices: newPractices,
                                        practice: newPractices[0] || ''
                                      }));
                                    }
                                  }}
                                  className="size-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                                />
                                <span className="text-sm text-gray-900">{name}</span>
                              </label>
                            ))}
                            {practices.filter(p => p.status === 'Active').length === 0 && (
                              <p className="text-sm text-gray-500 text-center py-2">No active practices available</p>
                            )}
                          </div>
                        ) : (
                          // Single practice (disabled) for Practice Administrators
                          <input
                            type="text"
                            value={formData.practice || userInfo.practice || ''}
                            disabled
                            className="block w-full rounded-md bg-gray-100 px-3 py-2 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 sm:text-sm"
                          />
                        )}
                        {(formData.practices || []).length > 0 && userInfo.role === 'System Administrator' && (
                          <p className="mt-2 text-xs text-gray-500">
                            {(formData.practices || []).length} practice{(formData.practices || []).length !== 1 ? 's' : ''} selected
                          </p>
                        )}
                      </div>
                  </div>
                )}

                  <div>
                    <label htmlFor="role" className="block text-sm font-medium text-gray-900">
                      Role <span className="text-red-500">*</span>
                    </label>
                    <div className="mt-2">
                  <select
                    id="role"
                    name="role"
                    value={formData.role || ''}
                    onChange={handleChange}
                    required
                        className="block w-full rounded-md bg-white px-3 py-2 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-indigo-600 sm:text-sm"
                  >
                    <option value="">Select Role</option>
                    {roles.map((role, index) => {
                      if (userInfo.role === 'Practice Administrator' && role === 'System Administrator') {
                        return null;
                      }
                      return <option key={index} value={role}>{role}</option>;
                    })}
                  </select>
                </div>
              </div>

                  <div>
                    <label htmlFor="status" className="block text-sm font-medium text-gray-900">
                      Status <span className="text-red-500">*</span>
                    </label>
                    <div className="mt-2">
                <select
                  id="status"
                  name="status"
                  value={formData.status || ''}
                  onChange={handleChange}
                  required
                        className="block w-full rounded-md bg-white px-3 py-2 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-indigo-600 sm:text-sm"
                >
                  <option value="">Select Status</option>
                  {statuses.map((status, index) => (
                    <option key={index} value={status}>{status}</option>
                  ))}
                </select>
              </div>
                  </div>
                </div>

                {/* User Type Selection */}
                <div className="border-t border-gray-200 pt-6">
                  <label className="block text-sm font-semibold text-gray-900 mb-4">
                    User Type
                    {isCreatingPhysicianUser && (
                      <span className="ml-2 text-xs font-normal text-indigo-600">(Locked to Physician)</span>
                    )}
                  </label>
                  <div className="space-y-4">
                    <div className="flex items-center">
                      <input
                        id="userType-office"
                        name="userType"
                        type="radio"
                        value="Office Staff"
                        checked={formData.userType === 'Office Staff'}
                        onChange={handleChange}
                        disabled={isCreatingPhysicianUser}
                        className="size-4 border-gray-300 text-indigo-600 focus:ring-indigo-600 disabled:opacity-50"
                      />
                      <label htmlFor="userType-office" className={`ml-3 text-sm font-medium ${isCreatingPhysicianUser ? 'text-gray-400' : 'text-gray-900'}`}>
                        Office Staff
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input
                        id="userType-physician"
                        name="userType"
                        type="radio"
                        value="Physician"
                        checked={formData.userType === 'Physician'}
                        onChange={handleChange}
                        disabled={isCreatingPhysicianUser}
                        className="size-4 border-gray-300 text-indigo-600 focus:ring-indigo-600 disabled:opacity-50"
                      />
                      <label htmlFor="userType-physician" className="ml-3 text-sm font-medium text-gray-900">
                        Physician
                      </label>
                    </div>
                  </div>
                </div>

                {/* Credentials Section (only for Physicians) */}
                {formData.userType === 'Physician' && (
                  <div className="border-t border-gray-200 pt-6">
                    <label className="block text-sm font-semibold text-gray-900 mb-4">
                      Physician Credentials
                    </label>
                    
                    <div className="space-y-6">
                      {/* Medical License */}
                      <div className="p-4 bg-gray-50 rounded-lg">
                        <label className="block text-sm font-medium text-gray-900 mb-3">Medical License</label>
                        <div className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2">
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">License Number</label>
                            <input
                              type="text"
                              value={formData.credentials?.medicalLicense?.number || ''}
                              onChange={(e) => handleCredentialChange('medicalLicense.number', e.target.value)}
                              placeholder="Enter license number"
                              className="block w-full rounded-md bg-white px-3 py-2 text-sm text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Expiration Date</label>
                            <input
                              type="date"
                              value={formData.credentials?.medicalLicense?.expirationDate || ''}
                              onChange={(e) => handleCredentialChange('medicalLicense.expirationDate', e.target.value)}
                              className="block w-full rounded-md bg-white px-3 py-2 text-sm text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Dental License */}
                      <div className="p-4 bg-gray-50 rounded-lg">
                        <label className="block text-sm font-medium text-gray-900 mb-3">Dental License</label>
                        <div className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2">
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">License Number</label>
                            <input
                              type="text"
                              value={formData.credentials?.dentalLicense?.number || ''}
                              onChange={(e) => handleCredentialChange('dentalLicense.number', e.target.value)}
                              placeholder="Enter license number"
                              className="block w-full rounded-md bg-white px-3 py-2 text-sm text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Expiration Date</label>
                            <input
                              type="date"
                              value={formData.credentials?.dentalLicense?.expirationDate || ''}
                              onChange={(e) => handleCredentialChange('dentalLicense.expirationDate', e.target.value)}
                              className="block w-full rounded-md bg-white px-3 py-2 text-sm text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600"
                            />
                          </div>
                        </div>
                      </div>

                      {/* DEA Certification */}
                      <div className="p-4 bg-gray-50 rounded-lg">
                        <label className="block text-sm font-medium text-gray-900 mb-3">DEA Certification</label>
                        <div className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2">
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">DEA Number</label>
                            <input
                              type="text"
                              value={formData.credentials?.deaCertification?.number || ''}
                              onChange={(e) => handleCredentialChange('deaCertification.number', e.target.value)}
                              placeholder="Enter DEA number"
                              className="block w-full rounded-md bg-white px-3 py-2 text-sm text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Expiration Date</label>
                            <input
                              type="date"
                              value={formData.credentials?.deaCertification?.expirationDate || ''}
                              onChange={(e) => handleCredentialChange('deaCertification.expirationDate', e.target.value)}
                              className="block w-full rounded-md bg-white px-3 py-2 text-sm text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600"
                            />
                          </div>
                        </div>
                      </div>

                      {/* CME Credit Hours */}
                      <div className="p-4 bg-gray-50 rounded-lg">
                        <label className="block text-sm font-medium text-gray-900 mb-3">Continuing Medical Education (CME)</label>
                        <div className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2">
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Credit Hours</label>
                            <input
                              type="number"
                              value={formData.credentials?.cme?.creditHours || ''}
                              onChange={(e) => handleCredentialChange('cme.creditHours', e.target.value)}
                              placeholder="Enter credit hours"
                              className="block w-full rounded-md bg-white px-3 py-2 text-sm text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Upload Certificate</label>
                            <input
                              type="file"
                              accept="image/*,.pdf"
                              onChange={(e) => {
                                const file = e.target.files[0];
                                if (file) {
                                  handleCMEImageUpload(file, false);
                                }
                                e.target.value = ''; // Reset input
                              }}
                              className="block w-full text-sm text-gray-900 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
                            />
                          </div>
                        </div>
                        {formData.credentials?.cme?.certificates && formData.credentials.cme.certificates.length > 0 && (
                          <div className="mt-4">
                            <label className="block text-xs font-medium text-gray-700 mb-2">Uploaded Certificates</label>
                            <div className="space-y-2">
                              {formData.credentials.cme.certificates.map((cert, index) => (
                                <div key={index} className="flex items-center justify-between p-2 bg-white rounded border border-gray-200">
                                  <a
                                    href={cert.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-sm text-indigo-600 hover:text-indigo-800 truncate flex-1"
                                  >
                                    {cert.fileName || `Certificate ${index + 1}`}
                                  </a>
                                  <button
                                    type="button"
                                    onClick={() => handleRemoveCMECertificate(index, cert.url, false)}
                                    className="ml-2 text-red-600 hover:text-red-800"
                                  >
                                    <FaTimes className="size-4" />
                                  </button>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Malpractice Insurance */}
                      <div className="p-4 bg-gray-50 rounded-lg">
                        <label className="block text-sm font-medium text-gray-900 mb-3">Malpractice Insurance</label>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Expiration Date</label>
                          <input
                            type="date"
                            value={formData.credentials?.malpracticeInsurance?.expirationDate || ''}
                            onChange={(e) => handleCredentialChange('malpracticeInsurance.expirationDate', e.target.value)}
                            className="block w-full rounded-md bg-white px-3 py-2 text-sm text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <div className="border-t border-gray-200 pt-6">
                  {isAddingUser ? (
                    <div className="grid grid-cols-1 gap-x-6 gap-y-6 sm:grid-cols-2">
                      <div>
                        <label htmlFor="tempPassword" className="block text-sm font-medium text-gray-900">
                          Temporary Password <span className="text-red-500">*</span>
                        </label>
                        <div className="mt-2">
                          <input
                            id="tempPassword"
                            name="tempPassword"
                            type="password"
                            value={formData.tempPassword || ''}
                            onChange={handleChange}
                            required
                            placeholder="Required for new users"
                            className="block w-full rounded-md bg-white px-3 py-2 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-indigo-600 sm:text-sm"
                          />
                        </div>
                      </div>
                      <div>
                        <label htmlFor="confirmTempPassword" className="block text-sm font-medium text-gray-900">
                          Confirm Temp Password
                        </label>
                        <div className="mt-2">
                          <input
                            id="confirmTempPassword"
                            name="confirmTempPassword"
                            type="password"
                            value={formData.confirmTempPassword || ''}
                            onChange={handleChange}
                            required
                            placeholder="Re-enter password"
                            className="block w-full rounded-md bg-white px-3 py-2 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-indigo-600 sm:text-sm"
                          />
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <p className="text-sm text-gray-600 mb-3">
                        Send a password reset link to this user. Their current password stays active until they set a new one via the link.
                      </p>
                      <button
                        type="button"
                        onClick={handleSendPasswordResetLink}
                        disabled={isSendingResetLink}
                        className="inline-flex items-center gap-x-2 rounded-md bg-white px-4 py-2.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isSendingResetLink ? (
                          <>
                            <span className="inline-block size-4 animate-spin rounded-full border-2 border-solid border-indigo-600 border-r-transparent"></span>
                            Sending...
                          </>
                        ) : (
                          'Send reset password link'
                        )}
                      </button>
                    </div>
                  )}
                </div>
              </div>

                </form>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                <button 
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    handleSubmitUser(e);
                  }}
                  className="inline-flex w-full justify-center rounded-md bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 sm:ml-3 sm:w-auto"
                >
                  {isAddingUser ? 'Add User' : 'Save Changes'}
                </button>
                <button 
                  type="button"
                  onClick={() => {
                    setIsEditingUser(false);
                    setIsAddingUser(false);
                    setIsCreatingPhysicianUser(false); // Reset physician-only mode
                    setOriginalCredentials(null); // Clear original credentials when canceling
                  }}
                  className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-4 py-2.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 outline-none sm:mt-0 sm:w-auto"
                >
                  Cancel
                </button>
              </div>
            </DialogPanel>
          </div>
        </div>
      </Dialog>

      {/* Physician Add Choice Dialog */}
      <Dialog open={showPhysicianChoiceDialog} onClose={() => setShowPhysicianChoiceDialog(false)} className="relative z-50">
        <DialogBackdrop
          transition
          className="fixed inset-0 bg-gray-500/75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
        />
        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <DialogPanel
              transition
              className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:w-full sm:max-w-md data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95"
            >
              <div className="bg-white px-4 pb-4 pt-5 sm:p-6">
                <div className="absolute right-0 top-0 hidden pr-4 pt-4 sm:block">
                  <button 
                    type="button"
                    onClick={() => setShowPhysicianChoiceDialog(false)}
                    className="rounded-md bg-white text-gray-400 hover:text-gray-500 outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                  >
                    <span className="sr-only">Close</span>
                    <FaTimes aria-hidden="true" className="size-6" />
                  </button>
                </div>
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex size-12 shrink-0 items-center justify-center rounded-full bg-indigo-100 sm:mx-0 sm:size-10">
                    <FaUserMd className="size-5 text-indigo-600" aria-hidden="true" />
                  </div>
                  <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                    <h3 className="text-base font-semibold text-gray-900">
                      Add Physician to Roster
                    </h3>
                    <p className="mt-2 text-sm text-gray-500">
                      Choose how you want to add this physician to your roster.
                    </p>
                  </div>
                </div>
                
                <div className="mt-6 space-y-3">
                  <button
                    type="button"
                    onClick={handleCreatePhysicianAccount}
                    className="relative w-full rounded-lg border border-gray-200 bg-white p-4 text-left hover:border-indigo-500 hover:bg-indigo-50 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-600 focus-visible:ring-offset-2"
                  >
                    <div className="flex items-center gap-4">
                      <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-indigo-100">
                        <FaUserPlus className="size-5 text-indigo-600" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-900">Create Account for Physician</p>
                        <p className="mt-1 text-xs text-gray-500">
                          Create a login account so the physician can access the system
                        </p>
                      </div>
                    </div>
                  </button>
                  
                  <button
                    type="button"
                    onClick={handleAddPhysicianToRosterOnly}
                    className="relative w-full rounded-lg border border-gray-200 bg-white p-4 text-left hover:border-indigo-500 hover:bg-indigo-50 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-600 focus-visible:ring-offset-2"
                  >
                    <div className="flex items-center gap-4">
                      <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-gray-100">
                        <FaUserMd className="size-5 text-gray-600" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-900">Add to Roster Only</p>
                        <p className="mt-1 text-xs text-gray-500">
                          Add physician to roster without creating a login account
                        </p>
                      </div>
                    </div>
                  </button>
                </div>
                
                <div className="mt-6">
                  <button
                    type="button"
                    onClick={() => setShowPhysicianChoiceDialog(false)}
                    className="w-full rounded-md bg-white px-4 py-2.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </DialogPanel>
          </div>
        </div>
      </Dialog>

      {/* Practice Edit Modal */}
      <Dialog open={isEditingPractice} onClose={() => setIsEditingPractice(false)} className="relative z-50">
        <DialogBackdrop
          transition
          className="fixed inset-0 bg-gray-500/75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
        />
        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <DialogPanel
              transition
              className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:w-full sm:max-w-2xl data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95"
            >
              <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                <div className="absolute right-0 top-0 hidden pr-4 pt-4 sm:block">
              <button 
                    type="button"
                onClick={() => setIsEditingPractice(false)}
                    className="rounded-md bg-white text-gray-400 hover:text-gray-500 outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
              >
                    <span className="sr-only">Close</span>
                    <FaTimes aria-hidden="true" className="size-6" />
              </button>
            </div>
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex size-12 shrink-0 items-center justify-center rounded-full bg-indigo-100 sm:mx-0 sm:size-10">
                    <FaBuilding aria-hidden="true" className="size-6 text-indigo-600" />
                  </div>
                  <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                    <h3 className="text-base font-semibold text-gray-900 mb-2">
                      {isAddingPractice ? 'Add Practice' : 'Edit Practice'}
                    </h3>
                  </div>
                </div>

                <form onSubmit={handleSubmitPractice} className="mt-6">
              <div className="space-y-6">
                <div>
                  <label htmlFor="practiceName" className="block text-sm font-medium text-gray-900">
                    Practice Name <span className="text-red-500">*</span>
                  </label>
                  <div className="mt-2">
                    <input
                      type="text"
                      id="practiceName"
                      name="practiceName"
                      value={formData.practiceName || ''}
                      onChange={handleChange}
                      required
                      placeholder="e.g. Memorial Oral Surgery"
                      className="block w-full rounded-md bg-white px-3 py-2 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-indigo-600 sm:text-sm"
                    />
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between">
                    <label className="block text-sm font-medium text-gray-900">
                      Office Locations & Contacts
                    </label>
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({
                        ...prev,
                        locations: [...(prev.locations || []), { name: '', adminLead: '', adminCell: '', adminEmail: '' }]
                      }))}
                      className="inline-flex items-center gap-1 rounded-md bg-indigo-50 px-2.5 py-1 text-xs font-medium text-indigo-700 ring-1 ring-inset ring-indigo-700/10 hover:bg-indigo-100"
                    >
                      <FaPlus className="size-2.5" /> Add Location
                    </button>
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    Each location can have its own admin contact. Leave location name blank for single-office practices.
                  </p>
                  <div className="mt-3 space-y-3">
                    {(formData.locations || []).map((loc, idx) => (
                      <div key={idx} className="rounded-md border border-gray-200 bg-gray-50/50 p-3 space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-medium text-gray-500">Location {idx + 1}</span>
                          {(formData.locations || []).length > 1 && (
                            <button
                              type="button"
                              onClick={() => setFormData(prev => ({
                                ...prev,
                                locations: prev.locations.filter((_, i) => i !== idx)
                              }))}
                              className="text-xs text-red-500 hover:text-red-700"
                            >
                              Remove
                            </button>
                          )}
                        </div>
                        <input
                          type="text"
                          value={loc.name || ''}
                          onChange={(e) => {
                            const updated = [...(formData.locations || [])];
                            updated[idx] = { ...updated[idx], name: e.target.value };
                            setFormData(prev => ({ ...prev, locations: updated }));
                          }}
                          placeholder="Location name (e.g. Cypress)"
                          className="block w-full rounded-md bg-white px-3 py-1.5 text-sm text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-indigo-600"
                        />
                        <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                          <input
                            type="text"
                            value={loc.adminLead || ''}
                            onChange={(e) => {
                              const updated = [...(formData.locations || [])];
                              updated[idx] = { ...updated[idx], adminLead: e.target.value };
                              setFormData(prev => ({ ...prev, locations: updated }));
                            }}
                            placeholder="Admin/Lead name"
                            className="block w-full rounded-md bg-white px-3 py-1.5 text-sm text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-indigo-600"
                          />
                          <input
                            type="tel"
                            value={loc.adminCell || ''}
                            onChange={(e) => {
                              const updated = [...(formData.locations || [])];
                              updated[idx] = { ...updated[idx], adminCell: formatPhoneNumber(e.target.value) };
                              setFormData(prev => ({ ...prev, locations: updated }));
                            }}
                            placeholder="Admin cell"
                            maxLength="14"
                            className="block w-full rounded-md bg-white px-3 py-1.5 text-sm text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-indigo-600"
                          />
                          <input
                            type="email"
                            value={loc.adminEmail || ''}
                            onChange={(e) => {
                              const updated = [...(formData.locations || [])];
                              updated[idx] = { ...updated[idx], adminEmail: e.target.value };
                              setFormData(prev => ({ ...prev, locations: updated }));
                            }}
                            placeholder="Admin email"
                            className="block w-full rounded-md bg-white px-3 py-1.5 text-sm text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-indigo-600"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-x-6 gap-y-6 sm:grid-cols-2">
                  <div>
                    <label htmlFor="officeAddress" className="block text-sm font-medium text-gray-900">
                      Office Address <span className="text-red-500">*</span>
                    </label>
                    <div className="mt-2">
                  <input 
                    id="officeAddress"
                    name="officeAddress" 
                        type="text"
                    value={formData.officeAddress || ''} 
                    onChange={handleChange} 
                    required
                          className="block w-full rounded-md bg-white px-3 py-2 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-indigo-600 sm:text-sm"
                  />
                </div>
                  </div>

                  <div>
                    <label htmlFor="officeCity" className="block text-sm font-medium text-gray-900">
                      City <span className="text-red-500">*</span>
                    </label>
                    <div className="mt-2">
                  <input 
                    id="officeCity"
                    name="officeCity" 
                        type="text"
                    value={formData.officeCity || ''} 
                    onChange={handleChange} 
                    required
                          className="block w-full rounded-md bg-white px-3 py-2 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-indigo-600 sm:text-sm"
                  />
                </div>
              </div>

                  <div>
                    <label htmlFor="officeState" className="block text-sm font-medium text-gray-900">
                      State <span className="text-red-500">*</span>
                    </label>
                    <div className="mt-2">
                  <input 
                    id="officeState"
                    name="officeState" 
                        type="text"
                    value={formData.officeState || ''} 
                    onChange={handleChange} 
                    required
                          className="block w-full rounded-md bg-white px-3 py-2 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-indigo-600 sm:text-sm"
                  />
                </div>
                  </div>

                  <div>
                    <label htmlFor="officeZipCode" className="block text-sm font-medium text-gray-900">
                      Zip Code <span className="text-red-500">*</span>
                    </label>
                    <div className="mt-2">
                  <input 
                    id="officeZipCode"
                    name="officeZipCode" 
                        type="text"
                    value={formData.officeZipCode || ''} 
                    onChange={handleChange} 
                    required
                          className="block w-full rounded-md bg-white px-3 py-2 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-indigo-600 sm:text-sm"
                  />
                </div>
              </div>

                  <div>
                    <label htmlFor="officePhone" className="block text-sm font-medium text-gray-900">
                      Phone <span className="text-red-500">*</span>
                    </label>
                    <div className="mt-2">
                  <input 
                    id="officePhone"
                    name="officePhone" 
                    type="tel"
                    value={formData.officePhone || ''} 
                    onChange={handleChange}
                    placeholder="(XXX) XXX-XXXX"
                    maxLength="14"
                    required
                          className="block w-full rounded-md bg-white px-3 py-2 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-indigo-600 sm:text-sm"
                  />
                </div>
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-900">
                      Email <span className="text-red-500">*</span>
                    </label>
                    <div className="mt-2">
                  <input 
                    id="email"
                    name="email" 
                    type="email"
                    value={formData.email || ''} 
                    onChange={handleChange} 
                    required
                          className="block w-full rounded-md bg-white px-3 py-2 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-indigo-600 sm:text-sm"
                  />
                </div>
              </div>

                  <div>
                    <label htmlFor="status" className="block text-sm font-medium text-gray-900">
                      Status <span className="text-red-500">*</span>
                    </label>
                    <div className="mt-2">
                <select
                  id="status"
                  name="status"
                  value={formData.status || ''}
                  onChange={handleChange}
                  required
                        className="block w-full rounded-md bg-white px-3 py-2 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-indigo-600 sm:text-sm"
                >
                  <option value="">Select Status</option>
                  {statuses.map((status, index) => (
                    <option key={index} value={status}>{status}</option>
                  ))}
                </select>
              </div>
                  </div>
                </div>
              </div>
                </form>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    handleSubmitPractice(e);
                  }}
                  className="inline-flex w-full justify-center rounded-md bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 sm:ml-3 sm:w-auto"
                >
                  Save Changes
                </button>
                <button 
                  type="button" 
                  onClick={() => setIsEditingPractice(false)} 
                  className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-4 py-2.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 outline-none sm:mt-0 sm:w-auto"
                >
                  Cancel
                </button>
              </div>
            </DialogPanel>
          </div>
        </div>
      </Dialog>

      {/* Physician Add/Edit Modal */}
      <Dialog open={isEditingPhysician} onClose={() => {
        setIsEditingPhysician(false);
        setIsAddingPhysician(false);
      }} className="relative z-50">
        <DialogBackdrop
          transition
          className="fixed inset-0 bg-gray-500/75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
        />
        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <DialogPanel
              transition
              className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:w-full sm:max-w-lg data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95"
            >
              <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                <div className="absolute right-0 top-0 hidden pr-4 pt-4 sm:block">
                  <button 
                    type="button"
                    onClick={() => {
                      setIsEditingPhysician(false);
                      setIsAddingPhysician(false);
                    }}
                    className="rounded-md bg-white text-gray-400 hover:text-gray-500 outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                  >
                    <span className="sr-only">Close</span>
                    <FaTimes aria-hidden="true" className="size-6" />
                  </button>
                </div>
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex size-12 shrink-0 items-center justify-center rounded-full bg-indigo-100 sm:mx-0 sm:size-10">
                    <FaUserMd aria-hidden="true" className="size-6 text-indigo-600" />
                  </div>
                  <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                    <h3 className="text-base font-semibold text-gray-900 mb-2">
                      {isAddingPhysician ? 'Add Physician to Roster' : 'Edit Physician'}
                    </h3>
                    <p className="text-sm text-gray-500">
                      {isAddingPhysician 
                        ? 'Add a physician to your practice roster. If a physician with the same email already exists in another practice, they will be linked to this practice as well.'
                        : 'Update physician information for this practice.'}
                    </p>
                  </div>
                </div>

                <form onSubmit={handleSubmitPhysician} className="mt-6">
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2">
                      <div>
                        <label htmlFor="physician-firstName" className="block text-sm font-medium text-gray-900">
                          First Name <span className="text-red-500">*</span>
                        </label>
                        <div className="mt-2">
                          <input 
                            id="physician-firstName"
                            name="firstName" 
                            type="text"
                            value={physicianFormData.firstName || ''} 
                            onChange={handlePhysicianFormChange} 
                            required
                            className="block w-full rounded-md bg-white px-3 py-2 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-indigo-600 sm:text-sm"
                          />
                        </div>
                      </div>

                      <div>
                        <label htmlFor="physician-lastName" className="block text-sm font-medium text-gray-900">
                          Last Name <span className="text-red-500">*</span>
                        </label>
                        <div className="mt-2">
                          <input 
                            id="physician-lastName"
                            name="lastName" 
                            type="text"
                            value={physicianFormData.lastName || ''} 
                            onChange={handlePhysicianFormChange} 
                            required
                            className="block w-full rounded-md bg-white px-3 py-2 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-indigo-600 sm:text-sm"
                          />
                        </div>
                      </div>
                    </div>

                    <div>
                      <label htmlFor="physician-email" className="block text-sm font-medium text-gray-900">
                        Email Address
                        {isAddingPhysician && (
                          <span className="ml-2 text-xs font-normal text-gray-500">
                            (Used to link physicians across practices)
                          </span>
                        )}
                      </label>
                      <div className="mt-2">
                        <input 
                          id="physician-email"
                          name="email" 
                          type="email"
                          value={physicianFormData.email || ''} 
                          onChange={handlePhysicianFormChange}
                          placeholder="physician@example.com"
                          className="block w-full rounded-md bg-white px-3 py-2 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-indigo-600 sm:text-sm"
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="physician-specialty" className="block text-sm font-medium text-gray-900">
                        Specialty
                      </label>
                      <div className="mt-2">
                        <input 
                          id="physician-specialty"
                          name="specialty" 
                          type="text"
                          value={physicianFormData.specialty || ''} 
                          onChange={handlePhysicianFormChange}
                          placeholder="e.g., Oral & Maxillofacial Surgery"
                          className="block w-full rounded-md bg-white px-3 py-2 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-indigo-600 sm:text-sm"
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="physician-npi" className="block text-sm font-medium text-gray-900">
                        NPI Number
                      </label>
                      <div className="mt-2">
                        <input 
                          id="physician-npi"
                          name="npi" 
                          type="text"
                          value={physicianFormData.npi || ''} 
                          onChange={handlePhysicianFormChange}
                          placeholder="10-digit NPI number"
                          maxLength="10"
                          className="block w-full rounded-md bg-white px-3 py-2 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-indigo-600 sm:text-sm"
                        />
                      </div>
                    </div>

                    {userInfo.role === 'System Administrator' && (
                      <div>
                        <label htmlFor="physician-practice" className="block text-sm font-medium text-gray-900">
                          Practice <span className="text-red-500">*</span>
                        </label>
                        <div className="mt-2">
                          <select
                            id="physician-practice"
                            name="practice"
                            value={physicianFormData.practice || ''}
                            onChange={handlePhysicianFormChange}
                            required
                            className="block w-full rounded-md bg-white px-3 py-2 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-indigo-600 sm:text-sm"
                          >
                            <option value="">Select Practice</option>
                            {[...new Set(practices.map(p => (p.practiceName || '').trim()).filter(Boolean))].sort().map((name) => (
                              <option key={name} value={name}>{name}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    )}

                    <div>
                      <label htmlFor="physician-status" className="block text-sm font-medium text-gray-900">
                        Status <span className="text-red-500">*</span>
                      </label>
                      <div className="mt-2">
                        <select
                          id="physician-status"
                          name="status"
                          value={physicianFormData.status || 'Active'}
                          onChange={handlePhysicianFormChange}
                          required
                          className="block w-full rounded-md bg-white px-3 py-2 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-indigo-600 sm:text-sm"
                        >
                          <option value="Active">Active</option>
                          <option value="Inactive">Inactive</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </form>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    handleSubmitPhysician(e);
                  }}
                  className="inline-flex w-full justify-center rounded-md bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 sm:ml-3 sm:w-auto"
                >
                  {isAddingPhysician ? 'Add Physician' : 'Save Changes'}
                </button>
                <button 
                  type="button" 
                  onClick={() => {
                    setIsEditingPhysician(false);
                    setIsAddingPhysician(false);
                  }} 
                  className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-4 py-2.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 outline-none sm:mt-0 sm:w-auto"
                >
                  Cancel
                </button>
              </div>
            </DialogPanel>
          </div>
        </div>
      </Dialog>

      {/* Profile Edit Modal */}
      <Dialog open={isEditingProfile} onClose={() => {
        // Refresh profileData from Firestore when modal closes without saving
        const refreshProfileData = async () => {
          const auth = getAuth();
          const user = auth.currentUser;
          if (user) {
            try {
              const userDocRef = doc(db, 'users', user.uid);
              const userDocSnap = await getDoc(userDocRef);
              if (userDocSnap.exists()) {
                const userData = userDocSnap.data();
                let credentials = userData.credentials || {};
                if (userData.userType === 'Physician' && (!credentials || !credentials.medicalLicense)) {
                  credentials = initializePhysicianCredentials();
                }
                setProfileData({
                  firstName: userData.firstName || '',
                  lastName: userData.lastName || '',
                  email: userData.email || '',
                  mobile: formatPhoneNumber(userData.mobile || ''),
                  practice: userData.practice || '',
                  role: userData.role || '',
                  userType: userData.userType || 'Office Staff',
                  credentials: credentials
                });
              }
            } catch (error) {
              console.error('Error refreshing profile data:', error);
            }
          }
        };
        refreshProfileData();
        setOriginalCredentials(null); // Clear original credentials when canceling
        setIsEditingProfile(false);
      }} className="relative z-50">
        <DialogBackdrop
          transition
          className="fixed inset-0 bg-gray-500/75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
        />
        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <DialogPanel
              transition
              className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:w-full sm:max-w-2xl data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95"
            >
              <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                <div className="absolute right-0 top-0 hidden pr-4 pt-4 sm:block">
                  <button
                    type="button"
                    onClick={() => setIsEditingProfile(false)}
                    className="rounded-md bg-white text-gray-400 hover:text-gray-500 outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                  >
                    <span className="sr-only">Close</span>
                    <FaTimes aria-hidden="true" className="size-6" />
                  </button>
                </div>
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex size-12 shrink-0 items-center justify-center rounded-full bg-indigo-100 sm:mx-0 sm:size-10">
                    <FaUser aria-hidden="true" className="size-6 text-indigo-600" />
                  </div>
                  <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                    <h3 className="text-base font-semibold text-gray-900 mb-2">
                      Edit Personal Information
                    </h3>
                  </div>
                </div>

                <form onSubmit={handleUpdateProfile} className="mt-6">
                  <div className="space-y-6">
                    <div className="grid grid-cols-1 gap-x-6 gap-y-6 sm:grid-cols-2">
                      <div>
                        <label htmlFor="modal-firstName" className="block text-sm font-medium text-gray-900">
                          First Name <span className="text-red-500">*</span>
                        </label>
                        <div className="mt-2">
                          <input
                            id="modal-firstName"
                            name="firstName"
                            type="text"
                            value={profileData.firstName}
                            onChange={handleChange}
                            required
                            className="block w-full rounded-md bg-white px-3 py-2 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-indigo-600 sm:text-sm"
                          />
                        </div>
                      </div>

                      <div>
                        <label htmlFor="modal-lastName" className="block text-sm font-medium text-gray-900">
                          Last Name <span className="text-red-500">*</span>
                        </label>
                        <div className="mt-2">
                          <input
                            id="modal-lastName"
                            name="lastName"
                            type="text"
                            value={profileData.lastName}
                            onChange={handleChange}
                            required
                            className="block w-full rounded-md bg-white px-3 py-2 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-indigo-600 sm:text-sm"
                          />
                        </div>
                      </div>

                      <div>
                        <label htmlFor="modal-email" className="block text-sm font-medium text-gray-900">
                          Email
                        </label>
                        <div className="mt-2">
                          <input
                            id="modal-email"
                            name="email"
                            type="email"
                            value={profileData.email}
                            disabled
                            className="block w-full rounded-md bg-gray-50 px-3 py-2 text-base text-gray-500 outline outline-1 -outline-offset-1 outline-gray-300 sm:text-sm"
                          />
                        </div>
                      </div>

                      <div>
                        <label htmlFor="modal-mobile" className="block text-sm font-medium text-gray-900">
                          Mobile <span className="text-red-500">*</span>
                        </label>
                        <div className="mt-2">
                          <input
                            id="modal-mobile"
                            name="mobile"
                            type="tel"
                            value={profileData.mobile}
                            onChange={handleChange}
                            placeholder="(XXX) XXX-XXXX"
                            maxLength="14"
                            required
                            className="block w-full rounded-md bg-white px-3 py-2 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-indigo-600 sm:text-sm"
                          />
                        </div>
                      </div>

                      <div className="sm:col-span-2">
                        <label htmlFor="modal-practice" className="block text-sm font-medium text-gray-900">
                          Practice{(profileData.practices?.length > 1 || userInfo.role === 'System Administrator') ? 's' : ''}
                        </label>
                        <div className="mt-2">
                          {userInfo.role === 'System Administrator' ? (
                            // System Admins can select multiple practices
                            <div className="space-y-2">
                              <div className="max-h-40 overflow-y-auto rounded-md border border-gray-300 bg-white p-2">
                                {practices.length > 0 ? (
                                  [...new Set(practices.map(p => (p.practiceName || '').trim()).filter(Boolean))].sort().map((name) => (
                                    <label key={name} className="flex items-center py-1 px-2 hover:bg-gray-50 rounded cursor-pointer">
                                      <input
                                        type="checkbox"
                                        checked={profileData.practices?.some(p => {
                                          const n = normalizePracticeName(name);
                                          const up = normalizePracticeName(String(p || ''));
                                          return up === n || up.startsWith(n + ' (');
                                        }) || false}
                                        onChange={(e) => {
                                          const currentPractices = profileData.practices || [];
                                          const n = normalizePracticeName(name);
                                          if (e.target.checked) {
                                            const hasMatch = currentPractices.some(p => normalizePracticeName(String(p)) === n || normalizePracticeName(String(p)).startsWith(n + ' ('));
                                            if (hasMatch) return;
                                            setProfileData({
                                              ...profileData,
                                              practices: [...currentPractices, name],
                                              practice: currentPractices.length === 0 ? name : profileData.practice
                                            });
                                          } else {
                                            const newPractices = currentPractices.filter(p => {
                                              const up = normalizePracticeName(String(p || ''));
                                              return up !== n && !up.startsWith(n + ' (');
                                            });
                                            setProfileData({
                                              ...profileData,
                                              practices: newPractices,
                                              practice: newPractices[0] || ''
                                            });
                                          }
                                        }}
                                        className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                                      />
                                      <span className="ml-2 text-sm text-gray-700">{name}</span>
                                    </label>
                                  ))
                                ) : (
                                  <p className="text-sm text-gray-500 py-2 px-2">No practices available in this organization. Create a practice first.</p>
                                )}
                              </div>
                              {profileData.practices?.length > 0 && (
                                <p className="text-xs text-gray-500">
                                  Selected: {profileData.practices.join(', ')}
                                </p>
                              )}
                            </div>
                          ) : profileData.practices && profileData.practices.length > 1 ? (
                            <div className="rounded-md bg-gray-50 px-3 py-2 text-sm text-gray-500 outline outline-1 -outline-offset-1 outline-gray-300">
                              {profileData.practices.map((practice, index) => (
                                <div key={index} className={index > 0 ? 'mt-1' : ''}>
                                  {practice}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <input
                              id="modal-practice"
                              name="practice"
                              type="text"
                              value={profileData.practices?.[0] || profileData.practice || ''}
                              disabled
                              className="block w-full rounded-md bg-gray-50 px-3 py-2 text-base text-gray-500 outline outline-1 -outline-offset-1 outline-gray-300 sm:text-sm"
                            />
                          )}
                        </div>
                      </div>

                      <div>
                        <label htmlFor="modal-role" className="block text-sm font-medium text-gray-900">
                          Role
                        </label>
                        <div className="mt-2">
                          <input
                            id="modal-role"
                            name="role"
                            type="text"
                            value={profileData.role}
                            disabled
                            className="block w-full rounded-md bg-gray-50 px-3 py-2 text-base text-gray-500 outline outline-1 -outline-offset-1 outline-gray-300 sm:text-sm"
                          />
                        </div>
                      </div>

                      <div>
                        <label htmlFor="modal-userType" className="block text-sm font-medium text-gray-900">
                          User Type
                        </label>
                        <div className="mt-2">
                          <input
                            id="modal-userType"
                            name="userType"
                            type="text"
                            value={profileData.userType || userInfo?.userType || 'Office Staff'}
                            disabled
                            className="block w-full rounded-md bg-gray-50 px-3 py-2 text-base text-gray-500 outline outline-1 -outline-offset-1 outline-gray-300 sm:text-sm"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Credentials Section (only for Physicians) */}
                    {(profileData.userType === 'Physician' || userInfo?.userType === 'Physician') && (
                      <div className="border-t border-gray-200 pt-6">
                        <label className="block text-sm font-semibold text-gray-900 mb-4">
                          Physician Credentials
                        </label>
                        
                        <div className="space-y-6">
                          {/* Medical License */}
                          <div className="p-4 bg-gray-50 rounded-lg">
                            <label className="block text-sm font-medium text-gray-900 mb-3">Medical License</label>
                            <div className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2">
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">License Number</label>
                                <input
                                  type="text"
                                  value={profileData.credentials?.medicalLicense?.number || ''}
                                  onChange={(e) => handleProfileCredentialChange('medicalLicense.number', e.target.value)}
                                  placeholder="Enter license number"
                                  className="block w-full rounded-md bg-white px-3 py-2 text-sm text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600"
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Expiration Date</label>
                                <input
                                  type="date"
                                  value={profileData.credentials?.medicalLicense?.expirationDate || ''}
                                  onChange={(e) => handleProfileCredentialChange('medicalLicense.expirationDate', e.target.value)}
                                  className="block w-full rounded-md bg-white px-3 py-2 text-sm text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600"
                                />
                              </div>
                            </div>
                          </div>

                          {/* Dental License */}
                          <div className="p-4 bg-gray-50 rounded-lg">
                            <label className="block text-sm font-medium text-gray-900 mb-3">Dental License</label>
                            <div className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2">
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">License Number</label>
                                <input
                                  type="text"
                                  value={profileData.credentials?.dentalLicense?.number || ''}
                                  onChange={(e) => handleProfileCredentialChange('dentalLicense.number', e.target.value)}
                                  placeholder="Enter license number"
                                  className="block w-full rounded-md bg-white px-3 py-2 text-sm text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600"
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Expiration Date</label>
                                <input
                                  type="date"
                                  value={profileData.credentials?.dentalLicense?.expirationDate || ''}
                                  onChange={(e) => handleProfileCredentialChange('dentalLicense.expirationDate', e.target.value)}
                                  className="block w-full rounded-md bg-white px-3 py-2 text-sm text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600"
                                />
                              </div>
                            </div>
                          </div>

                          {/* DEA Certification */}
                          <div className="p-4 bg-gray-50 rounded-lg">
                            <label className="block text-sm font-medium text-gray-900 mb-3">DEA Certification</label>
                            <div className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2">
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">DEA Number</label>
                                <input
                                  type="text"
                                  value={profileData.credentials?.deaCertification?.number || ''}
                                  onChange={(e) => handleProfileCredentialChange('deaCertification.number', e.target.value)}
                                  placeholder="Enter DEA number"
                                  className="block w-full rounded-md bg-white px-3 py-2 text-sm text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600"
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Expiration Date</label>
                                <input
                                  type="date"
                                  value={profileData.credentials?.deaCertification?.expirationDate || ''}
                                  onChange={(e) => handleProfileCredentialChange('deaCertification.expirationDate', e.target.value)}
                                  className="block w-full rounded-md bg-white px-3 py-2 text-sm text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600"
                                />
                              </div>
                            </div>
                          </div>

                          {/* CME Credit Hours */}
                          <div className="p-4 bg-gray-50 rounded-lg">
                            <label className="block text-sm font-medium text-gray-900 mb-3">Continuing Medical Education (CME)</label>
                            <div className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2">
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Credit Hours</label>
                                <input
                                  type="number"
                                  value={profileData.credentials?.cme?.creditHours || ''}
                                  onChange={(e) => handleProfileCredentialChange('cme.creditHours', e.target.value)}
                                  placeholder="Enter credit hours"
                                  className="block w-full rounded-md bg-white px-3 py-2 text-sm text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600"
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Upload Certificate</label>
                                <input
                                  type="file"
                                  accept="image/*,.pdf"
                                  onChange={(e) => {
                                    const file = e.target.files[0];
                                    if (file) {
                                      handleCMEImageUpload(file, true);
                                    }
                                    e.target.value = ''; // Reset input
                                  }}
                                  className="block w-full text-sm text-gray-900 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
                                />
                              </div>
                            </div>
                            {profileData.credentials?.cme?.certificates && profileData.credentials.cme.certificates.length > 0 && (
                              <div className="mt-4">
                                <label className="block text-xs font-medium text-gray-700 mb-2">Uploaded Certificates</label>
                                <div className="space-y-2">
                                  {profileData.credentials.cme.certificates.map((cert, index) => (
                                    <div key={index} className="flex items-center justify-between p-2 bg-white rounded border border-gray-200">
                                      <a
                                        href={cert.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-sm text-indigo-600 hover:text-indigo-800 truncate flex-1"
                                      >
                                        {cert.fileName || `Certificate ${index + 1}`}
                                      </a>
                                      <button
                                        type="button"
                                        onClick={() => handleRemoveCMECertificate(index, cert.url, true)}
                                        className="ml-2 text-red-600 hover:text-red-800"
                                      >
                                        <FaTimes className="size-4" />
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Malpractice Insurance */}
                          <div className="p-4 bg-gray-50 rounded-lg">
                            <label className="block text-sm font-medium text-gray-900 mb-3">Malpractice Insurance</label>
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">Expiration Date</label>
                              <input
                                type="date"
                                value={profileData.credentials?.malpracticeInsurance?.expirationDate || ''}
                                onChange={(e) => handleProfileCredentialChange('malpracticeInsurance.expirationDate', e.target.value)}
                                className="block w-full rounded-md bg-white px-3 py-2 text-sm text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
              </div>
            </form>
          </div>
              <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    handleUpdateProfile(e);
                  }}
                  className="inline-flex w-full justify-center rounded-md bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 sm:ml-3 sm:w-auto"
                >
                  Save Changes
                </button>
                <button
                  type="button"
                  onClick={() => setIsEditingProfile(false)}
                  className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-4 py-2.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 outline-none sm:mt-0 sm:w-auto"
                >
                  Cancel
                </button>
        </div>
            </DialogPanel>
          </div>
        </div>
      </Dialog>

      {/* Change Password Modal */}
      <Dialog open={isChangingPassword} onClose={() => {
        setIsChangingPassword(false);
        setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      }} className="relative z-50">
        <DialogBackdrop
          transition
          className="fixed inset-0 bg-gray-500/75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
        />
        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <DialogPanel
              transition
              className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:w-full sm:max-w-lg data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95"
            >
              <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                <div className="absolute right-0 top-0 hidden pr-4 pt-4 sm:block">
                  <button
                    type="button"
                    onClick={() => {
                      setIsChangingPassword(false);
                      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
                    }}
                    className="rounded-md bg-white text-gray-400 hover:text-gray-500 outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                  >
                    <span className="sr-only">Close</span>
                    <FaTimes aria-hidden="true" className="size-6" />
                  </button>
                </div>
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex size-12 shrink-0 items-center justify-center rounded-full bg-indigo-100 sm:mx-0 sm:size-10">
                    <FaLock aria-hidden="true" className="size-6 text-indigo-600" />
                  </div>
                  <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                    <h3 className="text-base font-semibold text-gray-900 mb-2">
                      Change Password
                    </h3>
                  </div>
                </div>

                <form onSubmit={handleChangePassword} className="mt-6">
                  <div className="space-y-6">
                    <div>
                      <label htmlFor="modal-currentPassword" className="block text-sm font-medium text-gray-900">
                        Current Password <span className="text-red-500">*</span>
                      </label>
                      <div className="mt-2">
                        <input
                          id="modal-currentPassword"
                          name="currentPassword"
                          type="password"
                          value={passwordData.currentPassword}
                          onChange={handlePasswordChange}
                          required
                          className="block w-full rounded-md bg-white px-3 py-2 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-indigo-600 sm:text-sm"
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="modal-newPassword" className="block text-sm font-medium text-gray-900">
                        New Password <span className="text-red-500">*</span>
                      </label>
                      <div className="mt-2">
                        <input
                          id="modal-newPassword"
                          name="newPassword"
                          type="password"
                          value={passwordData.newPassword}
                          onChange={handlePasswordChange}
                          required
                          className="block w-full rounded-md bg-white px-3 py-2 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-indigo-600 sm:text-sm"
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="modal-confirmPassword" className="block text-sm font-medium text-gray-900">
                        Confirm New Password <span className="text-red-500">*</span>
                      </label>
                      <div className="mt-2">
                        <input
                          id="modal-confirmPassword"
                          name="confirmPassword"
                          type="password"
                          value={passwordData.confirmPassword}
                          onChange={handlePasswordChange}
                          required
                          className="block w-full rounded-md bg-white px-3 py-2 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-indigo-600 sm:text-sm"
                        />
                      </div>
                    </div>
                  </div>
                </form>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    handleChangePassword(e);
                  }}
                  className="inline-flex w-full justify-center rounded-md bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 sm:ml-3 sm:w-auto"
                >
                  Save Changes
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setIsChangingPassword(false);
                    setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
                  }}
                  className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-4 py-2.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 outline-none sm:mt-0 sm:w-auto"
                >
                  Cancel
                </button>
              </div>
            </DialogPanel>
          </div>
        </div>
      </Dialog>

      {/* Confirmation Modal for Bulk Deletes */}
      <Dialog open={confirmModal.open} onClose={() => setConfirmModal(prev => ({ ...prev, open: false }))} className="relative z-50">
        <DialogBackdrop className="fixed inset-0 bg-gray-500/75 transition-opacity" />
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
            <DialogPanel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
              <div className="sm:flex sm:items-start">
                <div className="mx-auto flex size-12 shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:size-10">
                  <svg className="size-6 text-red-600" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
                  </svg>
                </div>
                <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                  <h3 className="text-base font-semibold text-gray-900">{confirmModal.title}</h3>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500">{confirmModal.message}</p>
                  </div>
                </div>
              </div>
              <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={confirmModal.onConfirm}
                  className="inline-flex w-full justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 sm:ml-3 sm:w-auto"
                >
                  {confirmModal.confirmLabel || 'Delete'}
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmModal(prev => ({ ...prev, open: false }))}
                  className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                >
                  Cancel
                </button>
              </div>
            </DialogPanel>
          </div>
        </div>
      </Dialog>

      <ToastContainer position="bottom-right" />
    </div>
  );
};

export default Settings;
